import { useEffect, useRef, useState } from 'react';
import { api, IncomingBlob } from '../api';
import { ws } from '../ws';
import * as crypto from '../crypto';
import * as protocol from '../protocol';
import type { Session } from './Auth';
import FrequencyModal from '../components/FrequencyModal';
import ShareContactModal from '../components/ShareContactModal';
import CreateGroupModal from '../components/CreateGroupModal';
import StickerPicker from '../components/StickerPicker';
import RememberDeviceModal from '../components/RememberDeviceModal';
import MessageBubble from '../components/MessageBubble';
import CallScreen from '../components/CallScreen';
import * as webrtc from '../webrtc';
import * as storage from '../storage';
import { setLocale, getLocale, useTranslation, t } from '../i18n';
import { setupPushSubscription, clearPushSubscription, setNativeForegroundState } from '../push_client';
import Settings, { ensureSettings } from './Settings';
import { Badge } from '@capawesome/capacitor-badge';
import { pseudonymOf } from '../identity_freq';
import { enterFrequency, leaveFrequency } from '../freq_session';
import * as engineSend from '../messaging/send';
import * as engineReceive from '../messaging/receive';
import * as engineLocal from '../messaging/storage_local';
import * as archive from '../messaging/conv_archive';
import type {
  FrequencyState,
  ChatState,
  GroupState,
  ChatMessage,
  TextMessage,
  MediaMessage,
  ContactCardMessage,
  PubKeyCache,
  ActiveTarget,
} from './chat/types';
import {
  unreadOfChat,
  unreadOfGroup,
  humanizeError,
  kindFromContentType,
  uuid,
  serializeMessage,
  deserializeMessage,
} from './chat/helpers';

// Re-export para callers externos que importan tipos desde Chat.tsx.
export type { ChatState, GroupState, ChatMessage, BaseMessage, TextMessage, MediaMessage, ContactCardMessage } from './chat/types';

interface Props {
  /** Sesión activa (puede ser la base general o una pseudo-cuenta de freq). */
  session: Session;
  /** Sesión de la cuenta base (general). Se mantiene en memoria mientras el usuario
   *  está en una freq, para poder volver sin re-tipear el password. */
  baseSession: Session;
  /** Callback al swap de sesión activa (entrar/salir de freq). */
  onSwitchSession: (s: Session) => void;
  onLogout: () => void;
}

export default function Chat({ session, baseSession, onSwitchSession, onLogout }: Props) {
  useTranslation();
  // La 'frequency' ya no es estado interno. Es una derivada de la sesión activa:
  // si session.pseudoOf existe, estamos en una freq; si no, estamos en general.
  // El campo `key` queda en 0 bytes — el HMAC con freq key ya no se usa: el
  // enrutamiento ahora es por pseudonym de la pseudo-cuenta del peer.
  const frequency: FrequencyState | null = session.pseudoOf
    ? { name: session.pseudoOf.freqLabel, key: new Uint8Array(0) }
    : null;
  // setFrequency es un shim a onSwitchSession: cualquier 'cambio de freq' implica
  // un swap de sesión completo (re-auth como pseudo-cuenta o vuelta a base).
  // Se conserva la firma para evitar cambios masivos en callers; los pocos sitios
  // que llaman setFrequency se actualizan a llamar enter/leaveFrequency directamente.
  function setFrequency(_: FrequencyState | null) {
    throw new Error('setFrequency obsoleto: usa onSwitchSession con enterFrequency/leaveFrequency');
  }
  const [showFreqModal, setShowFreqModal] = useState(false);
  const [showShareContact, setShowShareContact] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  // Modo seleccion estilo WhatsApp: long-press en un chat de la sidebar entra
  // en seleccion. El Set guarda claves "kind:id".
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const inSelection = selection.size > 0;
  // Si selection > 0 y el usuario confirma, esto activa el modal.
  const [confirmDeleteMulti, setConfirmDeleteMulti] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [contactsSearch, setContactsSearch] = useState('');
  const [editingContact, setEditingContact] = useState<string | null>(null);
  const [confirmBlock, setConfirmBlock] = useState<{ id: string; unblock: boolean } | null>(null);
  const [editAliasDraft, setEditAliasDraft] = useState('');
  const [editNotesDraft, setEditNotesDraft] = useState('');
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [showRememberDevice, setShowRememberDevice] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [settings, setSettings] = useState<storage.SyphraxSettings>(() => ensureSettings(session.vault?.settings));
  const [contactProfiles, setContactProfiles] = useState<Record<string, storage.ContactProfileEntry>>(
    () => session.vault?.contactProfiles ?? (session as any).chatsInitial?.contactProfiles ?? {},
  );
  const [showSettings, setShowSettings] = useState(false);
  const [showConfirmForget, setShowConfirmForget] = useState(false);
  const [showPushIntro, setShowPushIntro] = useState(false);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const contactProfilesRef = useRef(contactProfiles);
  contactProfilesRef.current = contactProfiles;
  // Set de pares (freqKey + contactId) a los que YA enviamos nuestro perfil — para no spamear.
  const profileSentToRef = useRef<Set<string>>(new Set());
  // Conjunto de syphrax_ids confirmados por frecuencia. Se sincroniza con el blob del servidor.
  const confirmedRef = useRef<Map<string, Set<string>>>(new Map());
  // Para evitar que el debounce dispare una put inicial vacía antes de cargar.
  const prefsLoadedRef = useRef(false);
  // Timer de inactividad
  const inactivityTimerRef = useRef<number | null>(null);
  const [toasts, setToasts] = useState<Array<{ id: string; title: string; body: string }>>([]);
  const [replyTo, setReplyTo] = useState<{ id: string; preview: string; from: string } | null>(null);
  const [editingMessage, setEditingMessage] = useState<{ id: string; originalText: string } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  function toggleBubbleMenu(id: string) {
    setOpenMenuId((cur) => (cur === id ? null : id));
  }

  type ActiveCallState = {
    callId: string;
    peerId: string;
    peerPublicKey: Uint8Array;
    direction: 'incoming' | 'outgoing';
    hasVideo: boolean;
    state: 'ringing' | 'connecting' | 'connected' | 'ending';
    startedAt: string | null;
    micMuted: boolean;
    cameraOn: boolean;
    remoteStream: MediaStream | null;
    localStream: MediaStream | null;
  };
  const [activeCall, setActiveCall] = useState<ActiveCallState | null>(null);
  const activeCallRef = useRef<ActiveCallState | null>(null);
  activeCallRef.current = activeCall;
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  const incomingOfferRef = useRef<{ callId: string; senderId: string; senderPk: Uint8Array; sdp: string; hasVideo: boolean } | null>(null);
  const [activePresence, setActivePresence] = useState<{
    online: boolean;
    last_seen_in_freq: string | null;
    visibility: storage.Visibility;
    sameFrequency: boolean;
  } | null>(null);
  const [vaultPin, setVaultPin] = useState<string | null>(session.vaultPin ?? null);
  const vaultPinRef = useRef<string | null>(vaultPin);
  vaultPinRef.current = vaultPin;
  const [chatsByFreq, setChatsByFreq] = useState<Record<string, Record<string, ChatState>>>(() => {
    const init = (session as any).chatsInitial?.chatsByFreq;
    if (!init || typeof init !== 'object') return {};
    const out: Record<string, Record<string, ChatState>> = {};
    for (const [freqKey, contacts] of Object.entries(init)) {
      // Defensa: solo aceptamos __general__. Cualquier otra freq en el blob es un
      // residuo histórico y no debería existir bajo la cuenta base.
      if (freqKey !== '__general__') continue;
      out[freqKey] = {};
      for (const [contactId, c] of Object.entries(contacts as Record<string, any>)) {
        try {
          out[freqKey][contactId] = {
            contactId: c.contactId,
            contactPublicKey: crypto.b64decode(c.contactPublicKey),
            messages: (c.messages || []).map(deserializeMessage),
            confirmed: c.confirmed ?? false,
          };
        } catch (_e) { /* skip corrupt */ }
      }
    }
    return out;
  });
  // Paginación: último timestamp procesado por (receiver_tag, freq_tag combo). Persiste en localStorage.
  const lastFetchedAtRef = useRef<Record<string, string>>({});
  const chatsByFreqRef = useRef<Record<string, Record<string, ChatState>>>(chatsByFreq);
  chatsByFreqRef.current = chatsByFreq;
  const [groupsByFreq, setGroupsByFreq] = useState<Record<string, Record<string, GroupState>>>(() => {
    const init = (session as any).chatsInitial?.groupsByFreq;
    if (!init || typeof init !== 'object') return {};
    const out: Record<string, Record<string, GroupState>> = {};
    for (const [freqKey, groups] of Object.entries(init)) {
      if (freqKey !== '__general__') continue;
      out[freqKey] = {};
      for (const [gid, g] of Object.entries(groups as Record<string, any>)) {
        try {
          out[freqKey][gid] = {
            id: g.id, name: g.name,
            members: (g.members || []).map((m: any) => ({ syphraxId: m.syphraxId, publicKey: crypto.b64decode(m.publicKey) })),
            messages: (g.messages || []).map(deserializeMessage),
          };
        } catch (_e) { /* skip */ }
      }
    }
    return out;
  });
  const groupsByFreqRef = useRef<Record<string, Record<string, GroupState>>>(groupsByFreq);
  groupsByFreqRef.current = groupsByFreq;
  // F1.2e hotfix — set de promesas en vuelo que el logout debe esperar a que terminen.
  const pendingWritesRef = useRef<Set<Promise<unknown>>>(new Set());
  // v34 legacy: ya no se usa. Mantenido como ref muerto para compat de imports residuales.
  // @ts-expect-error v200 unused
  const lastRatchetSessionsBlobRef = useRef<string | null>(null);
  // Upload chain serializado para ratchet sessions. Sin esto, dos uploads paralelos
  // pueden llegar en orden inverso al server y dejar el blob persistido con un state
  // viejo. Síntoma observado: 1-3/30 mensajes perdidos en ráfaga rápida tras recargar
  // (el cliente activo va bien, pero el state del server quedaba atrasado).

  const [active, setActive] = useState<ActiveTarget>(null);
  const activeRef = useRef<ActiveTarget>(null);
  activeRef.current = active;
  const [newContact, setNewContact] = useState('');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const frequencyRef = useRef<FrequencyState | null>(frequency);
  frequencyRef.current = frequency;
  // pseudoOfRef: acceso síncrono a la freq secret cuando hace falta computar
  // pseudonyms de peers en código que ya tiene un ref pattern.
  const pseudoOfRef = useRef<{ freqSecret: string; freqLabel: string } | undefined>(session.pseudoOf);
  pseudoOfRef.current = session.pseudoOf;
  const pubKeyCacheRef = useRef<PubKeyCache>(new Map());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const draftInputRef = useRef<HTMLInputElement | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  // currentFreqKey: siempre '__general__'. Cada sesión (base o pseudo) tiene su propio
  // bucket único en chatsByFreq, indexado bajo esta clave fija.
  const currentFreqKey = '__general__';
  const chats = chatsByFreq[currentFreqKey] ?? {};
  const groups = groupsByFreq[currentFreqKey] ?? {};

  useEffect(() => {
    ws.open(session.token);
    // Cursor `since` SOLO sobrevive si hay vault. Sin vault, el historial vive solo
    // en RAM — al recargar arranca vacío y necesita refetch completo. Si conserváramos
    // el cursor en localStorage acabaría huérfano y aparente "pérdida" del historial
    // (refetch traería 0 mensajes desde un punto reciente). Fix 2026-04-29.
    const hasLocalVault = !!session.vault || !!session.vaultPin;
    const cursorKey = 'syphrax_last_fetched_' + session.syphraxId;
    if (hasLocalVault) {
      try {
        const raw = localStorage.getItem(cursorKey);
        if (raw) lastFetchedAtRef.current = JSON.parse(raw);
      } catch (_e) {}
    } else {
      // Sin vault: limpiamos cualquier cursor previo y no lo usamos.
      try { localStorage.removeItem(cursorKey); } catch (_e) {}
      lastFetchedAtRef.current = {};
    }
    // Engine outbox: arranca el flush en background. Reintenta envíos pendientes
    // cada 5s y cuando se reconecta tras offline. Si POST falla, la entrada queda
    // en outbox y se vuelve a intentar (BUG B fix).
    const stopFlush = engineSend.startBackgroundFlush(getEngineSendCtx, 5000);
    const offReconnect = ws.onReconnect(() => {
      const ctx = getEngineSendCtx();
      if (ctx) engineSend.triggerImmediateFlush(ctx).catch(() => {});
    });
    return () => {
      stopFlush();
      offReconnect();
      ws.close();
    };
  }, [session.token]);

  // Modelo simple (2026-05-04): no hay history-sync entre devices porque no hay
  // devices con identidad propia. Cualquier device del usuario, con login + password,
  // descarga el archive replicado y reconstruye el historial completo.

  // Flush mejor-esfuerzo al cerrar pestaña/refrescar. En el modelo simple no hay
  // ratchet sessions que persistir; basta con vault local.
  useEffect(() => {
    const onBeforeUnload = () => {
      persistVaultNow().catch(() => {});
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (logs de estado eliminados — filtraban metadata útil para deanonimizar)


  // Modelo simple (2026-05-04): no hay prekeys ni signed prekeys ni OPKs. La cuenta
  // tiene una sola pareja X25519 publicada en /users/:id; los mensajes se cifran con
  // sealed_box. Sin estado per-session, sin replenish.

  // Auto re-registro push al cambiar de sesion (login / re-login).
  // Si el permiso ya fue concedido previamente, re-enlaza la suscripcion al
  // nuevo account_id sin que el usuario tenga que pulsar el boton de nuevo.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    if (!('PushManager' in window)) return;
    const token = session.token;
    const tag = crypto.b64encode(crypto.generalTag(session.syphraxId));
    setupPushSubscription(token, tag).then(r => {
      if (r.ok) {
        setPushEnabled(true);
        console.log('[PUSH] auto-reregistrado al login');
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.token]);

  // apply-vault-locale: si el vault trae un idioma guardado, aplicarlo.
  useEffect(() => {
    const lang = (settings as any).language;
    if (lang && lang !== getLocale()) setLocale(lang);
  }, [settings]);

  // VisualViewport: el body está fijado y dimensionado a estas variables; cuando aparece el teclado
  // virtual, vv.height baja y vv.offsetTop puede subir, por lo que el body se reposiciona y se reduce
  // automáticamente, manteniendo header, contenido y footer dentro del área visible.
  useEffect(() => {
    const root = document.documentElement;
    const vv = window.visualViewport;
    function update() {
      if (!vv) {
        root.style.setProperty("--vv-height", window.innerHeight + "px");
        root.style.setProperty("--vv-top", "0px");
        return;
      }
      root.style.setProperty("--vv-height", vv.height + "px");
      root.style.setProperty("--vv-top", vv.offsetTop + "px");
    }
    function lockScroll() {
      if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
    }
    update();
    lockScroll();
    if (vv) {
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
    } else {
      window.addEventListener("resize", update);
    }
    window.addEventListener("scroll", lockScroll, { passive: true });
    document.addEventListener("focusin", () => {
      setTimeout(() => { lockScroll(); update(); }, 50);
      setTimeout(() => { lockScroll(); update(); }, 250);
    });
    function onDocPointer() { setOpenMenuId(null); }
    document.addEventListener("pointerdown", onDocPointer);
    return () => {
      if (vv) {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
      } else {
        window.removeEventListener("resize", update);
      }
      window.removeEventListener("scroll", lockScroll);
      document.removeEventListener("pointerdown", onDocPointer);
    };
  }, []);

  // Timer de inactividad: si pasa el timeout configurado sin interacción y hay frecuencia activa, la cierra.
  useEffect(() => {
    function reset() {
      if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
      const seconds = settings.session.inactivityTimeoutSeconds;
      if (!seconds || !frequency) return;
      inactivityTimerRef.current = window.setTimeout(() => {
        setFrequency(null);
      }, seconds * 1000);
    }
    reset();
    const events: Array<keyof DocumentEventMap> = ['mousemove', 'keydown', 'pointerdown', 'touchstart'];
    for (const ev of events) document.addEventListener(ev, reset, { passive: true });
    return () => {
      if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
      for (const ev of events) document.removeEventListener(ev, reset);
    };
  }, [frequency, settings.session.inactivityTimeoutSeconds]);

  // Heartbeat de presencia: enviamos cada 25s mientras la pestaña esté activa y el setting permita visibilidad.
  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    async function pulse() {
      // Heartbeat de la sesion activa (base o pseudo). El server no ve freqs.
      const eff = storage.effectivePrivacy(settingsRef.current, null);
      const visibility = eff.showOnline;
      try {
        if (visibility === 'none') {
          await api.goOffline();
        } else {
          await api.heartbeat({ visibility });
        }
      } catch {
        // best-effort
      }
      if (!cancelled) timer = window.setTimeout(pulse, 25 * 1000);
    }
    pulse();
    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        api.goOffline().catch(() => {});
      } else {
        pulse();
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    // El offline al cerrar pestaña/PWA lo dispara el backend cuando detecta WS close
    // (ws/server.ts: ref counting onWsDisconnect + ping-pong zombie cada 30s).
    // No usamos sendBeacon: no puede llevar Authorization header.
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      api.goOffline().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency, settings.privacy.showOnline]);

  // Polling de presencia del contacto activo cada 15s.
  useEffect(() => {
    if (!active || active.kind !== 'contact') {
      setActivePresence(null);
      return;
    }
    const id = active.id;
    let cancelled = false;
    let timer: number | null = null;
    async function tick() {
      try {
        // Sin freq_tag en presence: si recibes datos del peer aqui, estais en
        // la misma identidad de freq por construccion (los pseudonyms coinciden).
        const myFreqTagB64 = null;
        const u = await api.getUser(id, myFreqTagB64);
        if (cancelled) return;
        if (u.presence) {
          const sameFrequency = true;
          void myFreqTagB64;
          setActivePresence({
            online: u.presence.online,
            last_seen_in_freq: u.presence.last_seen_in_freq,
            visibility: u.presence.visibility as storage.Visibility,
            sameFrequency,
          });
        }
      } catch {
        // ignore
      }
      if (!cancelled) timer = window.setTimeout(tick, 15 * 1000);
    }
    tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [active, frequency]);

  // Detecta si ya hay suscripción push activa para mostrar el estado correcto del botón.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushEnabled(!!sub);
      } catch {
        // ignore
      }
    })();
  }, []);

  // Helper único: convierte el payload descifrado de un archive_chunk en un
  // ChatMessage de la UI. UNIFICA la lógica que antes vivía en archive hydrate
  // y markChatReadInDb (las dos divergian de ingestIncoming/handleSelfCopy y
  // descartaban media/contact reales). Acepta los kinds reales del protocolo:
  // 'image'|'video'|'audio'|'file'|'contact'|'group_text'|'text_reply'.
  // Retorna null si el chunk no es un msg/media renderizable.
  function messageFromChunkPayload(p: archive.ArchivePayload, realId: string, peerId: string): ChatMessage | null {
    if (p.kind !== 'msg' && p.kind !== 'media') return null;
    const deliveredAt = p.delivered ? p.createdAt : undefined;
    const readAt = p.read ? p.createdAt : undefined;
    const fromSyphraxId = p.fromMe ? realId : peerId;
    let decoded: protocol.DecodedPayload;
    try { decoded = protocol.decodePayload(p.body); } catch { return null; }
    if (decoded.type === 'text') {
      return { id: p.messageId, fromMe: p.fromMe, kind: 'text', text: decoded.text, at: p.createdAt, deliveredAt, readAt, fromSyphraxId } as TextMessage;
    }
    if (decoded.type !== 'visible') return null;
    const v: any = decoded.payload;
    const replyMeta = v.reply_to;
    if (v.kind === 'contact') {
      return { id: p.messageId, fromMe: p.fromMe, kind: 'contact', contactSyphraxId: v.syphrax_id, at: p.createdAt, deliveredAt, readAt, fromSyphraxId, replyTo: replyMeta } as ContactCardMessage;
    }
    if (v.kind === 'group_text' || v.kind === 'text_reply') {
      return { id: p.messageId, fromMe: p.fromMe, kind: 'text', text: v.text ?? '', at: p.createdAt, deliveredAt, readAt, fromSyphraxId, replyTo: v.kind === 'text_reply' ? v.reply_to : replyMeta } as TextMessage;
    }
    // Media: kinds reales 'image'|'video'|'audio'|'file'.
    if (v.kind === 'image' || v.kind === 'video' || v.kind === 'audio' || v.kind === 'file') {
      return {
        id: p.messageId, fromMe: p.fromMe, kind: v.kind,
        mediaId: v.media_id, fileKey: crypto.b64decode(v.file_key),
        name: v.name, size: v.size, contentType: v.content_type,
        at: p.createdAt, deliveredAt, readAt, fromSyphraxId, replyTo: replyMeta,
      } as MediaMessage;
    }
    return null;
  }

  // Lectura/escritura directa en BD. Se llama desde el handler de click del
  // chat. Sin useEffect, sin estado local, sin races: lee chunks de BD, marca
  // read=true los del peer que no lo estén, recarga del state desde BD.
  async function markChatReadInDb(peerId: string) {
    const masterKey = sessionRef.current.wrappingKey;
    if (!masterKey) return;
    const realId = sessionRef.current.realSyphraxId;
    // freqLabel se usa SOLO para derivar conversation_tag del archive.
    const freqLabel = pseudoOfRef.current?.freqLabel ?? '__general__';
    // El state local SIEMPRE indexa bajo '__general__' (cada sesión, base o
    // pseudo, tiene su propio bucket). Si pones aquí freqLabel, setChatsByFreq
    // escribe en un bucket inexistente y el badge fantasma no se actualiza.
    const fk = '__general__';
    let chunks: archive.HydratedChunk[];
    try {
      chunks = await archive.hydrateConversation(masterKey, realId, peerId, freqLabel, { limit: 500 });
    } catch { return; }
    for (const ch of chunks) {
      if (ch.payload.fromMe) continue;
      if (ch.payload.read === true) continue;
      try { await archive.updateChunkFlags(masterKey, realId, peerId, freqLabel, ch.messageId, { read: true }); } catch {}
      try { await sendReadReceipt(peerId, ch.messageId); } catch {}
    }
    let updatedChunks: archive.HydratedChunk[];
    try {
      updatedChunks = await archive.hydrateConversation(masterKey, realId, peerId, freqLabel, { limit: 500 });
    } catch { return; }
    const messages: ChatMessage[] = [];
    for (const ch of updatedChunks) {
      const m = messageFromChunkPayload(ch.payload, realId, peerId);
      if (m) messages.push(m);
    }
    messages.sort((a, b) => (a.at || '').localeCompare(b.at || ''));
    setChatsByFreq((prev) => {
      const fc = prev[fk];
      if (!fc || !fc[peerId]) return prev;
      const cur = fc[peerId];
      const next = { ...prev, [fk]: { ...fc, [peerId]: { ...cur, messages } } };
      chatsByFreqRef.current = next;
      return next;
    });
  }

  // Comunica al service nativo el estado foreground/background de la WebView:
  //  - visible: el service deja de mostrar notifs (la UI ya los enseña) y resetea
  //    contador + badge.
  //  - hidden: el service vuelve a notificar mensajes entrantes con su contador.
  useEffect(() => {
    const sync = () => {
      const visible = document.visibilityState === 'visible';
      setNativeForegroundState(visible).catch(() => {});
    };
    document.addEventListener('visibilitychange', sync);
    sync();
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  // Badge del icono de la app: solo cuenta lo que el usuario PUEDE ver en el sidebar
  // ahora — chats y grupos de la frecuencia actual. Sumar entre frecuencias no tendría
  // sentido: el sidebar no muestra los chats de otras frecuencias, así que un badge
  // de fondo sería un fantasma sin chat asociado. En web el plugin es no-op.
  useEffect(() => {
    const fk = currentFreqKey;
    let total = 0;
    const fc = chatsByFreq[fk] ?? {};
    for (const c of Object.values(fc)) total += unreadOfChat(c);
    const fg = groupsByFreq[fk] ?? {};
    for (const g of Object.values(fg)) total += unreadOfGroup(g);
    (async () => {
      try {
        if (total > 0) await Badge.set({ count: total });
        else await Badge.clear();
      } catch (_e) { /* no-op fuera del shell nativo */ }
    })();
  }, [chatsByFreq, groupsByFreq, currentFreqKey]);

  // Carga el blob de preferencias del servidor (cifrado con sealed_box a nuestra propia pubkey).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await crypto.ready();
        const r = await api.getPreferences();
        if (cancelled) return;
        if (!r.blob) {
          prefsLoadedRef.current = true;
          return;
        }
        const cipher = crypto.b64decode(r.blob);
        let plain: string;
        try {
          plain = crypto.sealedBoxDecrypt(cipher, sessionRef.current.publicKey, sessionRef.current.privateKey);
        } catch {
          prefsLoadedRef.current = true;
          return;
        }
        const data = JSON.parse(plain) as {
          version: number;
          settings?: storage.SyphraxSettings;
          contactProfiles?: Record<string, storage.ContactProfileEntry>;
          confirmedContacts?: Record<string, string[]>;
        };
        if (data.settings) {
          // Defensa: el blob histórico podría traer overrides por frecuencia, fuera.
          const cleaned = {
            ...data.settings,
            profileByFrequency: {},
            privacyByFrequency: {},
          };
          setSettings(ensureSettings(cleaned));
        }
        if (data.contactProfiles) setContactProfiles((prev) => ({ ...data.contactProfiles, ...prev }));
        if (data.confirmedContacts) {
          // Solo aceptamos __general__. El resto de freqs no debe persistir bajo la cuenta base.
          const map = new Map<string, Set<string>>();
          for (const [freqKey, ids] of Object.entries(data.confirmedContacts)) {
            if (freqKey !== '__general__') continue;
            map.set(freqKey, new Set(ids));
          }
          confirmedRef.current = map;
          // Aplicar a chats existentes (los que llegaron antes de cargar prefs).
          setChatsByFreq((prev) => {
            const next: typeof prev = {};
            for (const [freqKey, byContact] of Object.entries(prev)) {
              next[freqKey] = {};
              const confSet = map.get(freqKey) ?? new Set();
              for (const [cid, c] of Object.entries(byContact)) {
                next[freqKey][cid] = { ...c, confirmed: c.confirmed || confSet.has(cid) };
              }
            }
            return next;
          });
        }
      } catch {
        // ignore
      } finally {
        prefsLoadedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persiste el blob de preferencias en el servidor con debounce 1.5s.
  useEffect(() => {
    if (!prefsLoadedRef.current) return;
    const handle = window.setTimeout(() => {
      try {
        // Sanitizamos: profileByFrequency y privacyByFrequency están keyed por nombre
        // de freq → fuera del blob. confirmedContacts: solo entradas de __general__.
        const sBase = settingsRef.current;
        const sanitizedSettings = {
          ...sBase,
          profileByFrequency: {},
          privacyByFrequency: {},
        };
        const generalConfirmed = confirmedRef.current.get('__general__');
        const data = {
          version: 1,
          settings: sanitizedSettings,
          contactProfiles: contactProfilesRef.current,
          confirmedContacts: generalConfirmed
            ? { __general__: Array.from(generalConfirmed) }
            : {},
        };
        const json = JSON.stringify(data);
        const cipher = crypto.sealedBoxEncrypt(json, sessionRef.current.publicKey);
        api.putPreferences(crypto.b64encode(cipher)).catch(() => {});
      } catch {
        // ignore
      }
    }, 1500);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, contactProfiles, chatsByFreq]);

  // Hidratación desde vault (al montar Chat).
  useEffect(() => {
    if (!session.vault) {
      // (sin log)
      return;
    }
    const v = session.vault;
    // (sin log: las keys de chats son los syphrax_id de contactos)
    // Cache de pubkeys.
    for (const [id, pkB64] of Object.entries(v.pubKeyCache ?? {})) {
      pubKeyCacheRef.current.set(id, crypto.b64decode(pkB64));
    }
    // Modelo simple (2026-05-04): vault no contiene signing/prekey/ratchet. La
    // identidad cripto de la cuenta es UN par X25519 que vive en el vault como
    // privateKey/publicKey y en el server cifrada con Argon2id del password.
    // Chats: vault almacena SOLO frecuencia general (post-refactor 2026-04-29).
    const hydratedChats: Record<string, Record<string, ChatState>> = { __general__: {} };
    for (const [contactId, c] of Object.entries(v.chats ?? {})) {
      hydratedChats.__general__[contactId] = {
        contactId: c.contactId,
        contactPublicKey: crypto.b64decode(c.contactPublicKey),
        messages: c.messages.map(deserializeMessage),
        confirmed: c.confirmed ?? true,
      };
    }
    setChatsByFreq(hydratedChats);
    // Grupos: solo frecuencia general en el vault.
    const hydratedGroups: Record<string, Record<string, GroupState>> = { __general__: {} };
    for (const [gid, g] of Object.entries(v.groups ?? {})) {
      hydratedGroups.__general__[gid] = {
          id: g.id,
          name: g.name,
          members: g.members.map((m) => ({ syphraxId: m.syphraxId, publicKey: crypto.b64decode(m.publicKey) })),
          messages: g.messages.map(deserializeMessage),
        };
    }
    setGroupsByFreq(hydratedGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hidratar el snapshot local cifrado (chatsByFreq + groupsByFreq + contactProfiles)
  // al montar. Este es el origen REAL del historial — sobrevive reloads/cierres.
  // Si hay vault legacy, ya se hidrató antes; este merge añade lo que esté en
  // local.snapshot (más fresco si el usuario ha conversado tras el último vault).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await engineLocal.loadSnapshot(sessionRef.current.realSyphraxId) as
          | { chatsByFreq?: any; groupsByFreq?: any; contactProfiles?: any } | null;
        if (cancelled || !snap) return;
        if (snap.chatsByFreq) {
          setChatsByFreq((prev) => mergeChatsByFreq(prev, snap.chatsByFreq));
        }
        if (snap.groupsByFreq) {
          setGroupsByFreq((prev) => mergeGroupsByFreq(prev, snap.groupsByFreq));
        }
        if (snap.contactProfiles && typeof snap.contactProfiles === 'object') {
          setContactProfiles((prev) => ({ ...snap.contactProfiles, ...prev }));
        }
      } catch (err) {
        console.warn('[snapshot] hydrate failed:', (err as Error).message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v113 — putChats removed: el cache server-side fue reemplazado por archive_chunks (v110+).

  // v113 — putRatchetSessions removed: el state ratchet vive sólo en device (v110+).

  useEffect(() => {
    let cancelled = false;
    setActive(null);
    ws.unsubscribeAll();

    // session.syphraxId YA es el username del server (real para base, pseudonym para freq).
    const myReceiverTag = crypto.generalTag(session.syphraxId);
    const tagB64 = crypto.b64encode(myReceiverTag);
    ws.subscribe(tagB64);

    async function refetch(silent: boolean) {
      try {
        const sinceKey = tagB64 + ':' + session.syphraxId;
        const since = lastFetchedAtRef.current[sinceKey];
        const r = await api.fetchMessages({
          receiver_tag: tagB64,
          freq_tag: null,
          since,
        });
        if (cancelled) return;
        // (sin log)
        let maxAt: string | undefined = since;
        for (const m of r.messages) {
          try {
            await ingestIncoming(m, !silent);
            if (!maxAt || m.created_at > maxAt) maxAt = m.created_at;
          } catch (ingestErr) {
            console.warn('[SYPHRAX] refetch: ingest of blob', m.id, 'failed:', (ingestErr as Error).message);
          }
        }
        if (maxAt && maxAt !== since) {
          lastFetchedAtRef.current[sinceKey] = maxAt;
          // SOLO persistimos cursor si hay vault. Ver useEffect de WS open arriba.
          const hasLocalVault = !!sessionRef.current.vault || !!sessionRef.current.vaultPin;
          if (hasLocalVault) {
            try {
              localStorage.setItem('syphrax_last_fetched_' + sessionRef.current.syphraxId, JSON.stringify(lastFetchedAtRef.current));
            } catch (_e) {}
          }
        }
      } catch (err) {
        if (!cancelled && !silent) setError(humanizeError(err as Error));
      }
    }

    refetch(true);

    // Cuando el WS reconecta o la pestaña vuelve a ser visible, refetch silencioso
    // para recoger mensajes que llegaron mientras estábamos desconectados.
    const offReconnect = ws.onReconnect(() => { refetch(true); });
    function onVisible() {
      if (document.visibilityState === 'visible') refetch(true);
    }
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      offReconnect();
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.token]);

  useEffect(() => {
    const off = ws.onMessage((payload) => {
      ingestIncoming(payload, true).catch((err) => setError(humanizeError(err as Error)));
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Con flex-direction: column-reverse, scrollTop=0 es el FONDO del chat (mensaje más reciente).
  // No necesitamos ningún auto-scroll programático: el navegador ancla el scroll de forma nativa.
  function onMessagesScroll() { /* no-op: la layout column-reverse se encarga */ }

  async function getOrFetchPublicKey(realSyphraxId: string): Promise<Uint8Array> {
    // Filosofia Syphrax: en una freq, el peer 'bob' aparece ante el server bajo su
    // pseudonym = pseudonymOf('bob', freq_secret). Pedimos su public_key bajo ese
    // username opaco. La cache se indexa por el lookupId real del server (no por el
    // syphrax_id humano) para evitar colisiones entre sesiones distintas.
    const pof = pseudoOfRef.current;
    const lookupId = pof ? pseudonymOf(realSyphraxId, pof.freqSecret) : realSyphraxId;
    const cached = pubKeyCacheRef.current.get(lookupId);
    if (cached) return cached;
    const u = await api.getUser(lookupId);
    const pk = crypto.b64decode(u.public_key);
    pubKeyCacheRef.current.set(lookupId, pk);
    return pk;
  }

  async function resolveSenderId(blob: IncomingBlob): Promise<string | null> {
    // Sealed sender ahora se aplica TAMBIÉN a frecuencia general. El sender_envelope
    // es la fuente de verdad. sender_tag legacy en general (utf8 del syphraxId) sigue
    // soportado solo para mensajes antiguos sin envelope.
    if (blob.sender_envelope) {
      try {
        return crypto.sealedBoxDecrypt(
          crypto.b64decode(blob.sender_envelope),
          sessionRef.current.publicKey,
          sessionRef.current.privateKey,
        );
      } catch {
        return null;
      }
    }
    // Fallback legacy: solo en frecuencia general el sender_tag era utf8(syphraxId).
    const fkey = frequencyRef.current;
    if (!fkey) {
      try {
        return crypto.decodeGeneralTag(crypto.b64decode(blob.sender_tag));
      } catch {
        return null;
      }
    }
    return null;
  }

  // Mutex por sessionKey: serializa CUALQUIER operación ratchet (encrypt o decrypt) para
  // una misma sesión. Sin esto: encrypt y decrypt en paralelo leen el mismo state inicial,
  // avanzan independientemente, y la última escritura del state sobrescribe a la otra →
  // mensajes/read receipts perdidos.

  // v016 — mapping Visibility cliente <-> server.
  function srvVisToClient(v: 'everyone' | 'contacts' | 'nobody' | undefined): 'all' | 'contacts' | 'none' {
    if (v === 'everyone') return 'all';
    if (v === 'nobody') return 'none';
    return 'contacts';
  }
  function clientVisToSrv(v: 'all' | 'contacts' | 'none'): 'everyone' | 'contacts' | 'nobody' {
    if (v === 'all') return 'everyone';
    if (v === 'none') return 'nobody';
    return 'contacts';
  }

  // v016 — sync con server-side blocks y privacy_settings al login.
  // Garantiza que el bloqueo es PERSISTENTE entre devices y server-enforced.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [blocksR, privR] = await Promise.all([api.listBlocks(), api.getPrivacy()]);
        if (cancelled) return;
        const serverBlocked = blocksR.blocks.map(b => b.blocked_username.toLowerCase());
        const priv = privR.privacy;
        setSettings((prev) => {
          const localBlocked = prev.blocked ?? [];
          const merged = Array.from(new Set([...localBlocked, ...serverBlocked]));
          return {
            ...prev,
            blocked: merged,
            privacy: { ...(prev.privacy ?? {}), sendReadReceipts: priv.read_receipts, showLastSeen: srvVisToClient(priv.last_seen), showOnline: srvVisToClient(priv.online_visibility) } as any,
          };
        });
      } catch (_e) { /* server sync best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [session.token]);

  // v016 — diff onChange de blocked: aplica block/unblock al server.
  const blockedRefSync = useRef<string[]>(settings.blocked ?? []);
  useEffect(() => {
    const prev = blockedRefSync.current;
    const next = settings.blocked ?? [];
    const added = next.filter(x => !prev.includes(x));
    const removed = prev.filter(x => !next.includes(x));
    blockedRefSync.current = next;
    (async () => {
      for (const u of added) { try { await api.block(u); } catch {} }
      for (const u of removed) { try { await api.unblock(u); } catch {} }
    })();
  }, [settings.blocked]);

  // v016 — diff onChange de privacy: PUT al server.
  const privacyRefSync = useRef<any>(settings.privacy ?? {});
  useEffect(() => {
    const cur = settings.privacy ?? {};
    const prev = privacyRefSync.current;
    const update: any = {};
    if (cur.sendReadReceipts !== prev.sendReadReceipts) update.read_receipts = !!cur.sendReadReceipts;
    if (cur.showLastSeen !== prev.showLastSeen && cur.showLastSeen) update.last_seen = clientVisToSrv(cur.showLastSeen);
    if (cur.showOnline !== prev.showOnline && cur.showOnline) update.online_visibility = clientVisToSrv(cur.showOnline);
    privacyRefSync.current = cur;
    if (Object.keys(update).length === 0) return;
    api.putPrivacy(update).catch(() => {});
  }, [settings.privacy]);

  // El locking ratchet ahora vive en messaging/sessions.ts (withSessionLock).

  // Construir el contexto del engine de mensajería simple (2026-05-04).
  // Una sola pareja X25519 por cuenta efectiva (real o pseudo). Sin device-identity.
  function getEngineSendCtx(): import('../messaging/send').SendContext | null {
    const s = sessionRef.current;
    if (!s.privateKey || !s.publicKey) return null;
    return {
      myPriv: s.privateKey,
      myPub: s.publicKey,
      mySyphraxId: s.syphraxId,
      realSyphraxId: s.realSyphraxId,
      pseudoFreqLabel: pseudoOfRef.current?.freqLabel ?? '__general__',
      pseudoFreqSecret: pseudoOfRef.current?.freqSecret ?? null,
      archiveMasterKey: s.wrappingKey ?? new Uint8Array(0),
    };
  }

  function getEngineReceiveCtx(): import('../messaging/receive').ReceiveContext | null {
    const s = sessionRef.current;
    if (!s.privateKey || !s.publicKey) return null;
    return {
      myPriv: s.privateKey,
      myPub: s.publicKey,
      mySyphraxId: s.syphraxId,
      realSyphraxId: s.realSyphraxId,
      pseudoFreqLabel: pseudoOfRef.current?.freqLabel ?? '__general__',
      archiveMasterKey: s.wrappingKey ?? new Uint8Array(0),
    };
  }

  // Marker objeto que devuelve buildBlobPayload tras enviar via engine. api.sendMessage
  // lo detecta y resuelve sin POST adicional (porque el engine ya envió).
  interface EngineSentMarker {
    __engine_sent: true;
    __engine_id: string;
    __engine_created_at: string;
    receiver_tag: string;
    sender_tag: string;
    freq_tag: null;
    nonce: string;
    ciphertext: string;
    sender_envelope: string | null;
    type: 'msg' | 'media' | 'ctrl';
    ttl_seconds: number;
  }

  // Reemplaza al buildBlobPayload viejo. Encripta + envía via engine (outbox
  // persistente + multi-device fan-out). Devuelve un marker que el api.sendMessage
  // detecta para no volver a enviar.
  async function buildBlobPayload(
    receiverId: string,
    _receiverPublicKey: Uint8Array,
    plaintext: string,
    type: 'msg' | 'media' | 'ctrl',
    ttlOverride?: number,
  ): Promise<EngineSentMarker> {
    void ttlOverride;
    void _receiverPublicKey;
    const ctx = getEngineSendCtx();
    if (!ctx) throw new Error('engine not initialized (session keys missing)');
    // Self-copy a uno mismo (multi-instancia: otras pestañas/devices de fas1 suscritas
    // a su receiver_tag reciben este blob via WS y pintan el mensaje en vivo).
    // En el modelo simple v200, el envío a uno mismo SÍ se hace: receiver = sender,
    // sealed_box(plaintext, miPub) lo descifra cualquier instancia de la misma cuenta.
    const entry = await engineSend.enqueue(ctx, {
      receiverSyphraxId: receiverId,
      plaintext,
      type,
    });
    const result = await engineSend.flushOne(ctx, entry);
    void result;
    // v016: el id devuelto AL CALLER es el messageId ULID del engine, NO el id
    // del primer blob fisico. Esto unifica el id de UI con el id del archive
    // y permite que devices nuevos vean el MISMO mensaje al hidratar.
    return {
      __engine_sent: true,
      __engine_id: entry.messageId,
      __engine_created_at: entry.createdAt,
      receiver_tag: '', sender_tag: '', freq_tag: null,
      nonce: '', ciphertext: '', sender_envelope: null, type, ttl_seconds: 0,
    };
  }

  function isBlocked(syphraxId: string): boolean {
    return settingsRef.current.blocked.includes(syphraxId.toLowerCase());
  }

  function isConfirmedContact(syphraxId: string): boolean {
    return chatsByFreq['__general__']?.[syphraxId]?.confirmed === true;
  }

  function buildMyProfilePayloadFor(receiverId: string): protocol.ProfilePayload | null {
    const freqName: string | null = null;  // post-rework
    const eff = storage.effectiveProfile(settingsRef.current, freqName);
    const priv = storage.effectivePrivacy(settingsRef.current, freqName);
    const confirmed = isConfirmedContact(receiverId);
    function audienceAllows(v: storage.Visibility): boolean {
      if (v === 'all') return true;
      if (v === 'contacts') return confirmed;
      return false;
    }
    const photo = audienceAllows(priv.showPhoto) ? eff.photoB64 : null;
    const info = audienceAllows(priv.showInfo) ? eff.info : '';
    // Si todo está restringido y el usuario tampoco tiene displayName, no merece la pena enviar nada.
    if (!eff.displayName && !photo && !info) return null;
    return {
      kind: 'profile',
      display_name: eff.displayName,
      photo_b64: photo,
      info,
      freq_name: freqName,
    };
  }

  async function sendMyProfileTo(receiverId: string, receiverPublicKey: Uint8Array) {
    if (isBlocked(receiverId)) return;
    const key = '__general__::' + receiverId;  // una sesion = un bucket
    if (profileSentToRef.current.has(key)) return;
    try {
      const profilePayload = buildMyProfilePayloadFor(receiverId);
      if (!profilePayload) {
        profileSentToRef.current.add(key);
        return;
      }
      const payload = protocol.encodeControl(profilePayload);
      const blob = await buildBlobPayload(receiverId, receiverPublicKey, payload, 'ctrl');
      await api.sendMessage(blob);
      profileSentToRef.current.add(key);
    } catch {
      // ignoramos: si falla, lo reintentaremos en el próximo envío.
    }
  }

  function broadcastProfileToCurrentFrequency() {
    profileSentToRef.current.clear();
    const freqKey = currentFreqKey;
    const recipients = new Set<string>();
    for (const c of Object.values(chatsByFreq[freqKey] ?? {})) recipients.add(c.contactId);
    for (const g of Object.values(groupsByFreq[freqKey] ?? {})) {
      for (const m of g.members) {
        if (m.syphraxId !== sessionRef.current.realSyphraxId) recipients.add(m.syphraxId);
      }
    }
    for (const id of recipients) {
      if (isBlocked(id)) continue;
      getOrFetchPublicKey(id).then((pk) => sendMyProfileTo(id, pk)).catch(() => {});
    }
  }

  async function sendSelfCopy(targetKind: 'contact' | 'group', targetId: string, groupId: string | null, innerPlaintext: string, messageId: string, at: string) {
    // YA NO es best-effort: si falla, propagamos. El caller (sendToContact/sendToGroup) await aquí,
    // así el usuario ve el error y el mensaje no aparece como entregado falsamente.
    const myId = sessionRef.current.realSyphraxId;
    const myPk = sessionRef.current.publicKey;
    const payload: protocol.SelfCopyPayload = {
      target_kind: targetKind,
      target_id: targetId,
      group_id: groupId ?? undefined,
      inner: innerPlaintext,
      message_id: messageId,
      at,
    };
    const plaintext = protocol.encodeSelfCopy(payload);
    // Self-copies: TTL 7 días. Suficiente para recuperar tras un logout/reinstalación
    // pero sin acumular indefinidamente en el inbox del usuario.
    const blob = await buildBlobPayload(myId, myPk, plaintext, 'ctrl', 60 * 60 * 24 * 7);
    await api.sendMessage(blob);
  }

  async function sendReadReceipt(senderId: string, messageId: string) {
    // Privacy: nunca enviar read receipts a contactos bloqueados (filtraría online status).
    if (isBlocked(senderId)) return;
    // Respetar el ajuste de privacidad: si el usuario desactivó tics, no enviar.
    const eff = storage.effectivePrivacy(settingsRef.current, null);
    if (!eff.sendReadReceipts) return;
    try {
      const pk = await getOrFetchPublicKey(senderId);
      const payload = protocol.encodeControl({ kind: 'read', message_id: messageId });
      // Read receipts: TTL 24h. No tiene sentido conservar más.
      const blob = await buildBlobPayload(senderId, pk, payload, 'ctrl', 60 * 60 * 24);
      await api.sendMessage(blob);
      // Persistir read=true en MI chunk del entrante. Awaited para evitar
      // que un logout inmediato lo deje sin completar.
      const masterKey = sessionRef.current.wrappingKey;
      if (masterKey) {
        try {
          await archive.updateChunkFlags(
            masterKey, sessionRef.current.realSyphraxId, senderId,
            pseudoOfRef.current?.freqLabel ?? '__general__',
            messageId, { read: true },
          );
        } catch { /* best-effort */ }
      }
    } catch {
      // best-effort
    }
  }

  // v016 — delivered receipt: confirmar al sender que la cuenta del receiver
  // recibio el blob. Distinto del read (que requiere abrir la conversacion).
  // Bloqueados NO reciben (privacy I6 — el bloqueado nunca debe ver senal).
  async function sendDeliveredReceipt(senderId: string, messageId: string) {
    if (!messageId) return;
    if (senderId === sessionRef.current.realSyphraxId) return; // self-copies no llevan delivery ack
    if (isBlocked(senderId)) return;
    try {
      const pk = await getOrFetchPublicKey(senderId);
      const payload = protocol.encodeControl({ kind: 'delivered', message_id: messageId });
      const blob = await buildBlobPayload(senderId, pk, payload, 'ctrl', 60 * 60 * 24);
      await api.sendMessage(blob);
      // delivered=true ya lo escribió receive.ts al ingestar el msg, pero por
      // robustez (idempotente) lo confirmamos aqui.
    } catch { /* best-effort */ }
  }

  function ensureContactState(senderId: string, publicKey: Uint8Array, freqKey: string, confirmed: boolean) {
    setChatsByFreq((prev) => {
      const freqChats = prev[freqKey] ?? {};
      if (freqChats[senderId]) return prev;
      // Si el blob de prefs ya marcaba a este contacto como confirmado, lo respetamos.
      const wasConfirmed = confirmedRef.current.get(freqKey)?.has(senderId) === true;
      return {
        ...prev,
        [freqKey]: {
          ...freqChats,
          [senderId]: { contactId: senderId, contactPublicKey: publicKey, messages: [], confirmed: confirmed || wasConfirmed },
        },
      };
    });
  }

  function confirmContact(syphraxId: string, freqKey: string) {
    setChatsByFreq((prev) => {
      const freqChats = prev[freqKey];
      if (!freqChats || !freqChats[syphraxId]) return prev;
      const cur = freqChats[syphraxId];
      if (cur.confirmed) return prev;
      return {
        ...prev,
        [freqKey]: { ...freqChats, [syphraxId]: { ...cur, confirmed: true } },
      };
    });
    // Mantener el set persistido en server.
    let set = confirmedRef.current.get(freqKey);
    if (!set) {
      set = new Set();
      confirmedRef.current.set(freqKey, set);
    }
    set.add(syphraxId);
    profileSentToRef.current.delete(`${freqKey}::${syphraxId}`);
    // Reenviamos el perfil ahora con la audiencia elevada (best-effort).
    getOrFetchPublicKey(syphraxId)
      .then((pk) => sendMyProfileTo(syphraxId, pk))
      .catch(() => { /* ignore */ });
  }

  // Append puro: añade el mensaje (idempotente por id). No mantiene contador
  // de unread — éste se deriva de `messages` vía unreadOfChat/unreadOfGroup.
  function appendToContact(senderId: string, publicKey: Uint8Array, freqKey: string, message: ChatMessage) {
    setChatsByFreq((prev) => {
      const freqChats = prev[freqKey] ?? {};
      const cur = freqChats[senderId] ?? { contactId: senderId, contactPublicKey: publicKey, messages: [], confirmed: false };
      if (cur.messages.some((x) => x.id === message.id)) {
        return prev;
      }
      return {
        ...prev,
        [freqKey]: {
          ...freqChats,
          [senderId]: { ...cur, contactPublicKey: publicKey, messages: [...cur.messages, message] },
        },
      };
    });
  }

  function appendToGroup(groupId: string, freqKey: string, message: ChatMessage) {
    setGroupsByFreq((prev) => {
      const freqGroups = prev[freqKey] ?? {};
      const cur = freqGroups[groupId];
      if (!cur) return prev;
      if (cur.messages.some((x) => x.id === message.id)) return prev;
      return {
        ...prev,
        [freqKey]: { ...freqGroups, [groupId]: { ...cur, messages: [...cur.messages, message] } },
      };
    });
  }

  // Helpers para merge de history_sync (otros devices propios envían su snapshot).
  function mergeChatsByFreq(
    cur: Record<string, Record<string, ChatState>>,
    incoming: Record<string, Record<string, any>>,
  ): Record<string, Record<string, ChatState>> {
    const out: Record<string, Record<string, ChatState>> = { ...cur };
    for (const [freqKey, contacts] of Object.entries(incoming)) {
      const curFreq = out[freqKey] ?? {};
      const newFreq: Record<string, ChatState> = { ...curFreq };
      for (const [contactId, raw] of Object.entries(contacts as Record<string, any>)) {
        try {
          const incomingMessages: ChatMessage[] = Array.isArray(raw.messages) ? raw.messages : [];
          const incomingPubB64 = typeof raw.contactPublicKey === 'string' ? raw.contactPublicKey : null;
          const existing = curFreq[contactId];
          if (existing) {
            // Archive es source of truth: si el id coincide, REEMPLAZAR el
            // mensaje existente con el del archive (que trae readAt/deliveredAt
            // correctos). Los nuevos del archive se añaden.
            const incomingById = new Map(incomingMessages.map((m) => [m.id, m]));
            const merged: ChatMessage[] = existing.messages.map((m) => incomingById.get(m.id) ?? m);
            const existingIds = new Set(existing.messages.map((m) => m.id));
            for (const m of incomingMessages) {
              if (!existingIds.has(m.id)) merged.push(m);
            }
            merged.sort((a, b) => (a.at || '').localeCompare(b.at || ''));
            newFreq[contactId] = { ...existing, messages: merged };
          } else if (incomingPubB64) {
            newFreq[contactId] = {
              contactId,
              contactPublicKey: crypto.b64decode(incomingPubB64),
              messages: incomingMessages,
              confirmed: !!raw.confirmed,
            };
          }
        } catch (_e) { /* skip contact corrupt */ }
      }
      out[freqKey] = newFreq;
    }
    return out;
  }

  function mergeGroupsByFreq(
    cur: Record<string, Record<string, GroupState>>,
    incoming: Record<string, Record<string, any>>,
  ): Record<string, Record<string, GroupState>> {
    const out: Record<string, Record<string, GroupState>> = { ...cur };
    for (const [freqKey, groups] of Object.entries(incoming)) {
      const curFreq = out[freqKey] ?? {};
      const newFreq: Record<string, GroupState> = { ...curFreq };
      for (const [groupId, raw] of Object.entries(groups as Record<string, any>)) {
        try {
          const existing = curFreq[groupId];
          const incomingMessages: ChatMessage[] = Array.isArray(raw.messages) ? raw.messages : [];
          if (existing) {
            const incomingById = new Map(incomingMessages.map((m) => [m.id, m]));
            const merged: ChatMessage[] = existing.messages.map((m) => incomingById.get(m.id) ?? m);
            const existingIds = new Set(existing.messages.map((m) => m.id));
            for (const m of incomingMessages) {
              if (!existingIds.has(m.id)) merged.push(m);
            }
            merged.sort((a, b) => (a.at || '').localeCompare(b.at || ''));
            newFreq[groupId] = { ...existing, messages: merged };
          } else if (Array.isArray(raw.members)) {
            newFreq[groupId] = {
              id: groupId,
              name: typeof raw.name === 'string' ? raw.name : '',
              members: raw.members.map((m: any) => ({
                syphraxId: m.syphraxId,
                publicKey: typeof m.publicKey === 'string' ? crypto.b64decode(m.publicKey) : new Uint8Array(0),
              })),
              messages: incomingMessages,
            } as GroupState;
          }
        } catch (_e) { /* skip group corrupt */ }
      }
      out[freqKey] = newFreq;
    }
    return out;
  }

  // ============= v016 — Hidratacion desde archive replicado server-side =============
  // Garantiza invariante I2: cualquier device nuevo del usuario, al loguear,
  // descarga TODO el historial del archive (cifrado E2E) y lo mergea con el local.
  // Idempotente: messageId ULID dedupea entre el archive y los chats ya en cache.
  useEffect(() => {
    let cancelled = false;
    if (!sessionRef.current.wrappingKey) return () => { cancelled = true; };
    (async () => {
      try {
        const masterKey = sessionRef.current.wrappingKey!;
        const realId = sessionRef.current.realSyphraxId;
        const hydrated = await archive.hydrateAllConversations(masterKey, { limitPerConv: 500 });
        if (cancelled) return;
        if (hydrated.byConversation.size === 0) return;
        const incoming: Record<string, Record<string, { contactPublicKey: string | null; messages: ChatMessage[]; confirmed: boolean }>> = {};
        const newPeerByFreq: Array<{ freqKey: string; peerId: string }> = [];
        for (const { peerSyphraxId, freqLabel: _freqLabel, chunks } of hydrated.byConversation.values()) {
          // El state local SIEMPRE indexa bajo '__general__' (cada sesión, base
          // o pseudo, vive en su propio state). freqLabel del archive es solo
          // metadata para conv_tag — NO se usa como key del state local.
          void _freqLabel;
          const fk = '__general__';
          if (!incoming[fk]) incoming[fk] = {};
          const existing = chatsByFreqRef.current[fk]?.[peerSyphraxId];
          const messages: ChatMessage[] = [];
          for (const ch of chunks) {
            const m = messageFromChunkPayload(ch.payload, realId, peerSyphraxId);
            if (m) messages.push(m);
          }
          incoming[fk][peerSyphraxId] = {
            contactPublicKey: existing ? crypto.b64encode(existing.contactPublicKey) : null,
            messages,
            confirmed: existing ? existing.confirmed : true,
          };
          if (!existing) newPeerByFreq.push({ freqKey: fk, peerId: peerSyphraxId });
        }
        // Resolver public keys de peers que el device nuevo aun no conocia.
        for (const { freqKey: fk, peerId } of newPeerByFreq) {
          try {
            const u = await api.getUser(peerId);
            if (incoming[fk][peerId]) incoming[fk][peerId].contactPublicKey = u.public_key;
          } catch (_e) { /* peer borrado o desconocido: skip */ }
        }
        setChatsByFreq((prev) => mergeChatsByFreq(prev, incoming));
      } catch (err) {
        console.warn('[archive] hydrate failed', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.token]);

  async function handleSelfCopy(p: protocol.SelfCopyPayload, _blobId: string, freqKey: string) {
    // (sin log: target_id e inner contenían datos del usuario)
    const inner = protocol.decodePayload(p.inner);
    // (sin log: inner contenía contenido del mensaje)
    let message: ChatMessage | null = null;
    if (inner.type === 'text') {
      message = { id: p.message_id, fromMe: true, kind: 'text', text: inner.text, at: p.at, fromSyphraxId: sessionRef.current.realSyphraxId };
    } else if (inner.type === 'visible') {
      const v = inner.payload;
      const replyMeta = (v as any).reply_to ? (v as any).reply_to : undefined;
      if (v.kind === 'contact') {
        message = { id: p.message_id, fromMe: true, kind: 'contact', contactSyphraxId: v.syphrax_id, at: p.at, fromSyphraxId: sessionRef.current.realSyphraxId, replyTo: replyMeta };
      } else if (v.kind === 'group_text') {
        message = { id: p.message_id, fromMe: true, kind: 'text', text: v.text, at: p.at, fromSyphraxId: sessionRef.current.realSyphraxId, replyTo: replyMeta };
      } else if (v.kind === 'text_reply') {
        message = { id: p.message_id, fromMe: true, kind: 'text', text: v.text, at: p.at, fromSyphraxId: sessionRef.current.realSyphraxId, replyTo: v.reply_to };
      } else {
        message = {
          id: p.message_id, fromMe: true, kind: v.kind,
          mediaId: v.media_id, fileKey: crypto.b64decode(v.file_key),
          name: v.name, size: v.size, contentType: v.content_type,
          at: p.at, fromSyphraxId: sessionRef.current.realSyphraxId,
          replyTo: replyMeta,
        };
      }
    } else {
      return;
    }
    if (!message) {
      console.warn('[SYPHRAX] handleSelfCopy: message is null, skipping');
      return;
    }
    if (p.target_kind === 'group' && p.group_id) {
      // (sin log)
      appendToGroup(p.group_id, freqKey, message);
    } else if (p.target_kind === 'contact') {
      try {
        // (sin log)
        const pk = await getOrFetchPublicKey(p.target_id);
        console.log('[SYPHRAX] handleSelfCopy: got pubkey, ensuring + appending');
        ensureContactState(p.target_id, pk, freqKey, false);
        appendToContact(p.target_id, pk, freqKey, message);
        // (sin log)
      } catch (err) {
        console.warn('[SYPHRAX] handleSelfCopy failed:', (err as Error).message);
      }
    }
  }

  async function ingestIncoming(m: IncomingBlob, notify: boolean = true) {
    // Sin filtro de freq_tag: cada sesion solo recibe lo destinado a su receiver_tag.

    let senderId: string | null = null;
    let publicKey: Uint8Array;
    let plain: string = '';

    if (m.nonce === '') {
      // ===== RATCHET path via engine (rediseño 2026-05-03) =====
      // El engine descifra el sealed_sender con la device key (no la account key
      // legacy), descifra el ratchet con la sesión (peerSyphraxId, peerDeviceId,
      // freqLabel) y persiste el state en IndexedDB. Devuelve senderSyphraxId.
      const rctx = getEngineReceiveCtx();
      if (!rctx) return;
      const result = await engineReceive.ingestLive(rctx, m);
      if (!result) return;
      senderId = result.senderSyphraxId;
      plain = result.plaintext;
      publicKey = await getOrFetchPublicKey(senderId);
    } else {
      // ===== LEGACY path =====
      senderId = await resolveSenderId(m);
      if (!senderId) return;
      publicKey = await getOrFetchPublicKey(senderId);
      try {
        plain = crypto.decryptMessage(
          crypto.b64decode(m.ciphertext),
          crypto.b64decode(m.nonce),
          publicKey,
          sessionRef.current.privateKey,
        );
      } catch {
        return;
      }
    }

    const decoded = protocol.decodePayload(plain);
    const freqKey = '__general__';
    // v016 — preferir messageId logico (ULID del sender) sobre blob.id fisico.
    // Esto unifica el id local con el id en el archive replicado entre devices.
    const msgKey: string = (m as any).message_id ?? m.id;

    if (decoded.type === 'control') {
      handleControlPayload(senderId, publicKey, freqKey, decoded.payload, notify, m.created_at);
      return;
    }

    if (decoded.type === 'self_copy') {
      // (sin log: blob.id no es sensible pero el contexto sí)
      // Solo aceptamos self-copies cifradas por nosotros mismos.
      if (senderId !== sessionRef.current.realSyphraxId) {
        console.warn('[SYPHRAX] self-copy senderId mismatch (rejected)');
        return;
      }
      handleSelfCopy(decoded.payload, m.id, freqKey);
      return;
    }

    // Bloqueados: descartamos cualquier mensaje visible del que esté bloqueado.
    if (isBlocked(senderId)) return;

    let message: ChatMessage;
    let groupId: string | undefined;
    if (decoded.type === 'text') {
      message = { id: msgKey, fromMe: false, kind: 'text', text: decoded.text, at: m.created_at, fromSyphraxId: senderId };
    } else if (decoded.type === 'visible') {
      const p = decoded.payload;
      groupId = p.group_id;
      const replyMeta = (p as any).reply_to ? (p as any).reply_to : undefined;
      if (p.kind === 'contact') {
        message = {
          id: msgKey, fromMe: false, kind: 'contact',
          contactSyphraxId: p.syphrax_id, at: m.created_at, fromSyphraxId: senderId,
          replyTo: replyMeta,
        };
      } else if (p.kind === 'group_text') {
        message = { id: msgKey, fromMe: false, kind: 'text', text: p.text, at: m.created_at, fromSyphraxId: senderId, replyTo: replyMeta };
      } else if (p.kind === 'text_reply') {
        message = { id: msgKey, fromMe: false, kind: 'text', text: p.text, at: m.created_at, fromSyphraxId: senderId, replyTo: p.reply_to };
      } else {
        message = {
          id: msgKey, fromMe: false, kind: p.kind,
          mediaId: p.media_id,
          fileKey: crypto.b64decode(p.file_key),
          name: p.name, size: p.size, contentType: p.content_type,
          at: m.created_at, fromSyphraxId: senderId,
          replyTo: replyMeta,
        };
      }
    } else {
      return;
    }

    // Si el chat del peer está abierto, marcamos read=true directamente en
    // el chunk (BD). El estado local se reconstruye al re-hidratar; aquí no
    // mantenemos readAt local — fuente única = chunks en BD.
    const myFreqName = '__general__';  // post-rework
    const a = activeRef.current;
    const lookingAtThis =
      freqKey === myFreqName && (
        (groupId && a?.kind === 'group' && a.id === groupId) ||
        (!groupId && a?.kind === 'contact' && a.id === senderId)
      );

    if (groupId && groupsByFreqRef.current[freqKey]?.[groupId]) {
      appendToGroup(groupId, freqKey, message);
    } else if (groupId) {
      // Mensaje de grupo cuyo invite no llegó (descartado por desync ratchet típicamente).
      // Pedimos al sender que reenvíe el invite. El reinvite trae los miembros y crea el grupo.
      window.setTimeout(async () => {
        try {
          const pk = await getOrFetchPublicKey(senderId);
          const blob = await buildBlobPayload(senderId, pk, protocol.encodeControl({ kind: 'request_invite', group_id: groupId } as any), 'ctrl', 60);
          await api.sendMessage(blob);
        } catch (_e) { /* ignore */ }
      }, 0);
      // Y guardamos el mensaje en chat 1-1 con el sender como fallback temporal.
      ensureContactState(senderId, publicKey, freqKey, false);
      appendToContact(senderId, publicKey, freqKey, message);
    } else {
      // Si no existía el contacto, lo creamos como NO confirmado (lo añadió el usuario al recibir).
      ensureContactState(senderId, publicKey, freqKey, false);
      appendToContact(senderId, publicKey, freqKey, message);
    }

    if (notify) notifyLocally(senderId, message, groupId);
    // v016 — receipts agregados:
    //   - delivered: SIEMPRE al consumir un blob de un peer (acuse de recepcion).
    //   - read: solo si lookingAtThis (chat abierto en este device).
    // await serializa la rafaga: si llegan 10 seguidos, los ctrls salen en orden.
    await sendDeliveredReceipt(senderId, msgKey);
    if (lookingAtThis) await sendReadReceipt(senderId, msgKey);
  }

  function previewOf(message: ChatMessage): string {
    if (message.kind === 'text') return message.text;
    if (message.kind === 'image') return t('preview.image');
    if (message.kind === 'video') return t('preview.video');
    if (message.kind === 'audio') return t('preview.audio');
    if (message.kind === 'contact') return `👤 ${message.contactSyphraxId}`;
    return `📎 ${message.name}`;
  }

  function pushToast(title: string, body: string) {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts((prev) => {
      const next = [...prev, { id, title, body }];
      return next.length > 3 ? next.slice(next.length - 3) : next;
    });
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }

  function notifyLocally(senderId: string, message: ChatMessage, groupId: string | undefined) {
    const n = settingsRef.current.notifications;
    // ¿Estoy mirando este chat justo ahora? Entonces no hace falta toast.
    const lookingAtChat =
      (active?.kind === 'contact' && active.id === senderId && !groupId) ||
      (active?.kind === 'group' && groupId && active.id === groupId);
    if (!lookingAtChat) {
      const title = groupId ? t('toast.group', displayNameOf(senderId)) : displayNameOf(senderId);
      const body = n.preview ? previewOf(message) : t('toast.newMessage');
      pushToast(title, body);
    }
    if (n.vibration && 'vibrate' in navigator) {
      try { navigator.vibrate(80); } catch { /* ignore */ }
    }
    if (n.sound) {
      try {
        const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
        const ctx = new Ctor();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = 880;
        g.gain.value = 0.05;
        o.connect(g); g.connect(ctx.destination);
        o.start();
        setTimeout(() => { o.stop(); ctx.close().catch(() => {}); }, 90);
      } catch { /* ignore */ }
    }
  }

  function handleControlPayload(
    senderId: string,
    senderPublicKey: Uint8Array,
    freqKey: string,
    payload: protocol.ControlPayload,
    notify: boolean,
    createdAt: string,
  ) {
    if (payload.kind === 'history_sync') {
      // Sólo aceptamos history_sync de NUESTROS PROPIOS devices.
      if (senderId !== sessionRef.current.realSyphraxId) return;
      try {
        const data = JSON.parse(payload.payload_json) as {
          chatsByFreq?: Record<string, Record<string, any>>;
          groupsByFreq?: Record<string, Record<string, any>>;
          contactProfiles?: Record<string, any>;
        };
        if (data.chatsByFreq) {
          setChatsByFreq((prev) => mergeChatsByFreq(prev, data.chatsByFreq!));
        }
        if (data.groupsByFreq) {
          setGroupsByFreq((prev) => mergeGroupsByFreq(prev, data.groupsByFreq!));
        }
        if (data.contactProfiles) {
          setContactProfiles((prev) => ({ ...data.contactProfiles, ...prev }));
        }
        console.warn('[history-sync] merged historial de otro device propio');
      } catch (err) {
        console.warn('[history-sync] decode failed:', (err as Error).message);
      }
      return;
    }
    if (payload.kind === 'request_invite') {
      // El peer perdió el invite (típicamente por desync ratchet). Reenviamos.
      const grp = groupsByFreqRef.current[freqKey]?.[payload.group_id];
      if (!grp) return; // no tenemos el grupo, nada que hacer
      const inviteMembers = grp.members.map((m) => ({
        syphrax_id: m.syphraxId,
        public_key: crypto.b64encode(m.publicKey),
      }));
      const reinvite = protocol.encodeControl({
        kind: 'group_invite',
        group_id: grp.id,
        group_name: grp.name,
        members: inviteMembers,
        freq_name: null,
      });
      window.setTimeout(async () => {
        try {
          const pk = await getOrFetchPublicKey(senderId);
          const blob = await buildBlobPayload(senderId, pk, reinvite, 'ctrl');
          await api.sendMessage(blob);
        } catch (_e) { /* ignore */ }
      }, 0);
      return;
    }
    if (payload.kind === 'sync_ping') {
      // El peer borró su sesión por wrong-secret-key. Acabamos de hacer X3DH responder
      // (gracias a v33), así que ya estamos sincronizados con su estado nuevo.
      // Reenviar mensajes recientes propios al peer (últimos 30s) sin readAt.
      const now = Date.now();
      const cutoff = 30 * 1000;
      const myMsgs = chatsByFreqRef.current[freqKey]?.[senderId]?.messages ?? [];
      const toResend = myMsgs.filter(m =>
        m.fromMe && !m.readAt && (now - new Date(m.at).getTime()) < cutoff && m.kind === 'text'
      );
      for (const m of toResend) {
        const text = (m as any).text;
        if (typeof text !== 'string') continue;
        window.setTimeout(async () => {
          try {
            // Texto plano: sin marker, decodePayload lo trata como text directo.
            const blob = await buildBlobPayload(senderId, senderPublicKey, text, 'msg');
            await api.sendMessage(blob);
          } catch (_e) { /* ignore */ }
        }, 0);
      }
      return;
    }
    if (payload.kind === 'delivered') {
      // v016: marca aspa 2 grises sin tocar readAt.
      const deliveredAt = new Date().toISOString();
      // Persistir delivered=true en MI chunk del saliente. Tras logout/login en
      // cualquier instancia el ✓✓ gris sigue pintado. IMPORTANTE: el freqLabel
      // del archive es el pseudoFreqLabel real (ALFA2025 si en freq), NO el
      // freqKey del state local (siempre '__general__').
      const masterKey = sessionRef.current.wrappingKey;
      const archiveFreqLabel = pseudoOfRef.current?.freqLabel ?? '__general__';
      if (masterKey && payload.message_id) {
        archive.updateChunkFlags(
          masterKey, sessionRef.current.realSyphraxId, senderId,
          archiveFreqLabel, payload.message_id, { delivered: true },
        ).catch(() => {});
      }
      setChatsByFreq((prev) => {
        const cur = prev[freqKey];
        if (!cur || !cur[senderId]) return prev;
        const chat = cur[senderId];
        const idx = chat.messages.findIndex((m) => m.id === payload.message_id && m.fromMe);
        if (idx < 0 || chat.messages[idx].deliveredAt) return prev;
        const newMessages = chat.messages.slice();
        newMessages[idx] = { ...newMessages[idx], deliveredAt };
        const next = { ...prev, [freqKey]: { ...cur, [senderId]: { ...chat, messages: newMessages } } };
        chatsByFreqRef.current = next;
        return next;
      });
      setGroupsByFreq((prev) => {
        const cur = prev[freqKey];
        if (!cur) return prev;
        let touched = false;
        const newGroups = { ...cur };
        for (const gid of Object.keys(cur)) {
          const g = cur[gid];
          const idx = g.messages.findIndex((m) => m.id === payload.message_id && m.fromMe);
          if (idx >= 0 && !g.messages[idx].deliveredAt) {
            touched = true;
            const nm = g.messages.slice();
            nm[idx] = { ...nm[idx], deliveredAt };
            newGroups[gid] = { ...g, messages: nm };
          }
        }
        if (!touched) return prev;
        const next = { ...prev, [freqKey]: newGroups };
        groupsByFreqRef.current = next;
        return next;
      });
      return;
    }
    if (payload.kind === 'read') {
      const readAt = new Date().toISOString();
      // Persistir read=true (y delivered=true por implicación) en MI chunk del saliente.
      // freqLabel del archive = pseudoFreqLabel (ALFA2025), NO freqKey local ('__general__').
      const masterKey = sessionRef.current.wrappingKey;
      const archiveFreqLabel = pseudoOfRef.current?.freqLabel ?? '__general__';
      if (masterKey && payload.message_id) {
        archive.updateChunkFlags(
          masterKey, sessionRef.current.realSyphraxId, senderId,
          archiveFreqLabel, payload.message_id, { read: true, delivered: true },
        ).catch(() => {});
      }
      // v016: read implica delivered. Si no habiamos marcado deliveredAt, lo marcamos tambien.
      // Aspas: 1 -> 2 grises -> 2 iluminadas. Idempotente: solo se marca la primera vez.
      setChatsByFreq((prev) => {
        const cur = prev[freqKey];
        if (!cur || !cur[senderId]) return prev;
        const chat = cur[senderId];
        const idx = chat.messages.findIndex((m) => m.id === payload.message_id && m.fromMe);
        if (idx < 0 || chat.messages[idx].readAt) return prev;
        const newMessages = chat.messages.slice();
        newMessages[idx] = { ...newMessages[idx], readAt, deliveredAt: newMessages[idx].deliveredAt ?? readAt };
        const next = { ...prev, [freqKey]: { ...cur, [senderId]: { ...chat, messages: newMessages } } };
        chatsByFreqRef.current = next;
        return next;
      });
      setGroupsByFreq((prev) => {
        const cur = prev[freqKey];
        if (!cur) return prev;
        let touched = false;
        const newGroups = { ...cur };
        for (const gid of Object.keys(cur)) {
          const g = cur[gid];
          const idx = g.messages.findIndex((m) => m.id === payload.message_id && m.fromMe);
          if (idx >= 0 && !g.messages[idx].readAt) {
            touched = true;
            const nm = g.messages.slice();
            nm[idx] = { ...nm[idx], readAt, deliveredAt: nm[idx].deliveredAt ?? readAt };
            newGroups[gid] = { ...g, messages: nm };
          }
        }
        if (!touched) return prev;
        const next = { ...prev, [freqKey]: newGroups };
        groupsByFreqRef.current = next;
        return next;
      });
      return;
    }
    if (payload.kind === 'group_invite') {
      // Sin filtro freq_name: si el invite llego, esta sesion es su freq.
      const eff = storage.effectivePrivacy(settingsRef.current, null);
      // Usamos ref síncrono: handleControlPayload puede ejecutarse antes de que el
      // setChatsByFreq de confirmContact (disparado por sendToContact en respuesta a un
      // mensaje del peer) propague al state visible vía closure.
      const senderConfirmed = chatsByFreqRef.current[freqKey]?.[senderId]?.confirmed === true;
      if (eff.whoAddsMeToGroups === 'none') return;
      if (eff.whoAddsMeToGroups === 'contacts' && !senderConfirmed) return;
      const members = payload.members.map((m) => ({
        syphraxId: m.syphrax_id,
        publicKey: crypto.b64decode(m.public_key),
      }));
      // Cacheamos pubkeys de miembros.
      for (const mem of members) pubKeyCacheRef.current.set(mem.syphraxId, mem.publicKey);
      // También aseguramos que el sender está cacheado.
      pubKeyCacheRef.current.set(senderId, senderPublicKey);
      setGroupsByFreq((prev) => {
        const freqGroups = prev[freqKey] ?? {};
        if (freqGroups[payload.group_id]) return prev;
        return {
          ...prev,
          [freqKey]: {
            ...freqGroups,
            [payload.group_id]: { id: payload.group_id, name: payload.group_name, members, messages: [] },
          },
        };
      });
      return;
    }
    if (payload.kind === 'call_offer') {
      // Descartar ofertas viejas (bootstrap o re-fetch) — solo aceptamos las que llegan en tiempo real.
      if (!notify) return;
      const ageMs = Date.now() - new Date(createdAt).getTime();
      if (ageMs > 30000) return;
      // Solo aceptamos llamadas si estamos en la misma frecuencia (general = null).
      // Sin filtro freq_name: el offer llega solo a quien deba (filosofia post-rework).
      // Si ya tenemos una llamada en curso → busy.
      if (activeCallRef.current) {
        sendCallCtrl(senderId, senderPublicKey, { kind: 'call_end', call_id: payload.call_id, reason: 'busy' });
        return;
      }
      incomingOfferRef.current = {
        callId: payload.call_id,
        senderId,
        senderPk: senderPublicKey,
        sdp: payload.sdp,
        hasVideo: payload.has_video,
      };
      const initial: ActiveCallState = {
        callId: payload.call_id,
        peerId: senderId,
        peerPublicKey: senderPublicKey,
        direction: 'incoming',
        hasVideo: payload.has_video,
        state: 'ringing',
        startedAt: null,
        micMuted: false,
        cameraOn: payload.has_video,
        remoteStream: null,
        localStream: null,
      };
      activeCallRef.current = initial;
      setActiveCall(initial);
      return;
    }
    if (payload.kind === 'call_answer') {
      const call = activeCallRef.current;
      if (!call || call.callId !== payload.call_id || !pcRef.current) return;
      pcRef.current.setRemoteDescription({ type: 'answer', sdp: payload.sdp }).then(async () => {
        for (const c of pendingIceRef.current) {
          try { await pcRef.current!.addIceCandidate(c); } catch (_e) { /* ignore */ }
        }
        pendingIceRef.current = [];
        setActiveCall((prev) => prev ? { ...prev, state: 'connecting' } : prev);
      }).catch(() => { cleanupCall(); });
      return;
    }
    if (payload.kind === 'call_ice') {
      const call = activeCallRef.current;
      if (!call || call.callId !== payload.call_id) return;
      const candidate: RTCIceCandidateInit = {
        candidate: payload.candidate,
        sdpMid: payload.sdp_mid ?? undefined,
        sdpMLineIndex: payload.sdp_m_line_index ?? undefined,
      };
      const pc = pcRef.current;
      if (pc && pc.remoteDescription) {
        pc.addIceCandidate(candidate).catch(() => { /* ignore */ });
      } else {
        pendingIceRef.current.push(candidate);
      }
      return;
    }
    if (payload.kind === 'call_end') {
      const call = activeCallRef.current;
      if (call && call.callId === payload.call_id) cleanupCall();
      return;
    }
    if (payload.kind === 'reaction') {
      const emoji = payload.emoji;
      const groupId = payload.group_id;
      function applyReaction(target: ChatMessage): ChatMessage {
        if (target.kind === 'contact' || target.kind === 'image' || target.kind === 'video' || target.kind === 'audio' || target.kind === 'file' || target.kind === 'text') {
          const reactions: Record<string, string[]> = { ...(target.reactions ?? {}) };
          // Quitar al senderId de TODAS las reacciones (un usuario solo puede tener UNA reacción a la vez)
          for (const e of Object.keys(reactions)) {
            reactions[e] = reactions[e].filter((id) => id !== senderId);
            if (reactions[e].length === 0) delete reactions[e];
          }
          if (emoji) {
            reactions[emoji] = [...(reactions[emoji] ?? []), senderId];
          }
          return { ...target, reactions } as ChatMessage;
        }
        return target;
      }
      if (groupId) {
        setGroupsByFreq((prev) => {
          const fg = prev[freqKey];
          if (!fg || !fg[groupId]) return prev;
          const g = fg[groupId];
          const idx = g.messages.findIndex((mm) => mm.id === payload.message_id);
          if (idx < 0) return prev;
          const updated = [...g.messages];
          updated[idx] = applyReaction(g.messages[idx]);
          return { ...prev, [freqKey]: { ...fg, [groupId]: { ...g, messages: updated } } };
        });
      } else {
        setChatsByFreq((prev) => {
          const fc = prev[freqKey];
          if (!fc || !fc[senderId]) return prev;
          const c = fc[senderId];
          const idx = c.messages.findIndex((mm) => mm.id === payload.message_id);
          if (idx < 0) return prev;
          const updated = [...c.messages];
          updated[idx] = applyReaction(c.messages[idx]);
          return { ...prev, [freqKey]: { ...fc, [senderId]: { ...c, messages: updated } } };
        });
      }
      return;
    }
    if (payload.kind === 'edit') {
      // Editar texto de un mensaje del que somos receptor (o que ya teníamos del emisor).
      const newText = payload.text;
      const editedAt = new Date().toISOString();
      const groupId = payload.group_id;
      if (groupId) {
        setGroupsByFreq((prev) => {
          const fg = prev[freqKey];
          if (!fg || !fg[groupId]) return prev;
          const g = fg[groupId];
          const idx = g.messages.findIndex((mm) => mm.id === payload.message_id && mm.fromSyphraxId === senderId);
          if (idx < 0) return prev;
          const target = g.messages[idx];
          if (target.kind !== 'text') return prev;
          const updated = [...g.messages];
          updated[idx] = { ...target, text: newText, editedAt };
          return { ...prev, [freqKey]: { ...fg, [groupId]: { ...g, messages: updated } } };
        });
      } else {
        setChatsByFreq((prev) => {
          const fc = prev[freqKey];
          if (!fc || !fc[senderId]) return prev;
          const c = fc[senderId];
          const idx = c.messages.findIndex((mm) => mm.id === payload.message_id);
          if (idx < 0) return prev;
          const target = c.messages[idx];
          if (target.kind !== 'text') return prev;
          const updated = [...c.messages];
          updated[idx] = { ...target, text: newText, editedAt };
          return { ...prev, [freqKey]: { ...fc, [senderId]: { ...c, messages: updated } } };
        });
      }
      return;
    }
    if (payload.kind === 'profile') {
      // Sin filtro freq_name: cada sesion solo recibe lo destinado a su receiver_tag.
      if (isBlocked(senderId)) return;
      setContactProfiles((prev) => ({
        ...prev,
        [senderId]: {
          displayName: payload.display_name,
          photoB64: payload.photo_b64,
          info: payload.info,
          receivedAt: new Date().toISOString(),
        },
      }));
      return;
    }
  }

  async function addContact() {
    const cleaned = newContact.trim().toLowerCase();
    if (!cleaned) return;
    if (cleaned === sessionRef.current.realSyphraxId.toLowerCase()) {
      setError(t('error.selfContact'));
      return;
    }
    if (isBlocked(cleaned)) {
      setError(t('error.blocked'));
      return;
    }
    setError(null);
    try {
      const publicKey = await getOrFetchPublicKey(cleaned);
      ensureContactState(cleaned, publicKey, currentFreqKey, true);
      // Si ya existía como no confirmado, lo confirmamos.
      confirmContact(cleaned, currentFreqKey);
      setActive({ kind: 'contact', id: cleaned });
      setNewContact('');
    } catch (err) {
      const e = err as Error;
      if (e.message.includes('HTTP 404') && pseudoOfRef.current) {
        setError(t('error.notInFreq', cleaned));
      } else {
        setError(humanizeError(e));
      }
    }
  }

  async function sendToContact(receiverId: string, plaintext: string, type: 'msg' | 'media', localMessage: ChatMessage) {
    if (isBlocked(receiverId)) {
      setError('Tienes a este usuario bloqueado.');
      return;
    }
    const pk = await getOrFetchPublicKey(receiverId);
    confirmContact(receiverId, currentFreqKey);
    sendMyProfileTo(receiverId, pk).catch(() => {});
    const blob = await buildBlobPayload(receiverId, pk, plaintext, type);
    const res = await api.sendMessage(blob);
    appendToContact(receiverId, pk, currentFreqKey, { ...localMessage, id: res.id, at: res.created_at });
    await sendSelfCopy('contact', receiverId, null, plaintext, res.id, res.created_at);
  }

  async function sendToGroup(group: GroupState, plaintext: string, type: 'msg' | 'media', localMessage: ChatMessage) {
    const recipients = group.members.filter((m) => m.syphraxId !== sessionRef.current.realSyphraxId && !isBlocked(m.syphraxId));
    let firstResId: string | null = null;
    let firstResAt: string | null = null;
    for (const member of recipients) {
      try {
        sendMyProfileTo(member.syphraxId, member.publicKey).catch(() => {});
        const blob = await buildBlobPayload(member.syphraxId, member.publicKey, plaintext, type);
        const res = await api.sendMessage(blob);
        if (!firstResId) {
          firstResId = res.id;
          firstResAt = res.created_at;
        }
      } catch (err) {
        console.warn('group send failed for', member.syphraxId, err);
      }
    }
    appendToGroup(group.id, currentFreqKey, {
      ...localMessage,
      id: firstResId ?? localMessage.id,
      at: firstResAt ?? localMessage.at,
    });
    if (firstResId && firstResAt) {
      await sendSelfCopy('group', group.id, group.id, plaintext, firstResId, firstResAt);
    }
  }

  async function send() {
    if (!active || !draft.trim()) return;
    if (active.kind === 'contact' && isBlocked(active.id)) return;
    setError(null);

    // Modo edición: enviamos un ctrl 'edit' al receptor (o miembros) y actualizamos local.
    if (editingMessage) {
      const newText = draft;
      const messageId = editingMessage.id;
      try {
        if (active.kind === 'contact') {
          const ctrlReal = protocol.encodeControl({ kind: 'edit', message_id: messageId, text: newText });
          const pk = await getOrFetchPublicKey(active.id);
          const blob = await buildBlobPayload(active.id, pk, ctrlReal, 'ctrl');
          await api.sendMessage(blob);
          setChatsByFreq((prev) => {
            const fc = prev[currentFreqKey];
            if (!fc || !fc[active.id]) return prev;
            const c = fc[active.id];
            const idx = c.messages.findIndex((mm) => mm.id === messageId);
            if (idx < 0) return prev;
            const target = c.messages[idx];
            if (target.kind !== 'text') return prev;
            const updated = [...c.messages];
            updated[idx] = { ...target, text: newText, editedAt: new Date().toISOString() };
            return { ...prev, [currentFreqKey]: { ...fc, [active.id]: { ...c, messages: updated } } };
          });
        } else {
          const group = groups[active.id];
          if (!group) return;
          const ctrlReal = protocol.encodeControl({ kind: 'edit', message_id: messageId, text: newText, group_id: group.id });
          for (const m of group.members) {
            if (m.syphraxId === sessionRef.current.realSyphraxId) continue;
            try {
              const blob = await buildBlobPayload(m.syphraxId, m.publicKey, ctrlReal, 'ctrl');
              await api.sendMessage(blob);
            } catch (err) { console.warn('edit failed for', m.syphraxId, err); }
          }
          setGroupsByFreq((prev) => {
            const fg = prev[currentFreqKey];
            if (!fg || !fg[group.id]) return prev;
            const g = fg[group.id];
            const idx = g.messages.findIndex((mm) => mm.id === messageId && mm.fromMe);
            if (idx < 0) return prev;
            const target = g.messages[idx];
            if (target.kind !== 'text') return prev;
            const updated = [...g.messages];
            updated[idx] = { ...target, text: newText, editedAt: new Date().toISOString() };
            return { ...prev, [currentFreqKey]: { ...fg, [group.id]: { ...g, messages: updated } } };
          });
        }
        setDraft('');
        setEditingMessage(null);
        setReplyTo(null);
      } catch (err) {
        setError(humanizeError(err as Error));
      }
      return;
    }

    // Envío normal con o sin reply.
    const replyContext = replyTo;
    try {
      if (active.kind === 'contact') {
        const localMessage: TextMessage = {
          id: 'local', fromMe: true, kind: 'text', text: draft, at: new Date().toISOString(),
          replyTo: replyContext ?? undefined,
        };
        const plaintext = replyContext
          ? protocol.encodeVisible({ kind: 'text_reply', text: draft, reply_to: replyContext })
          : draft;
        await sendToContact(active.id, plaintext, replyContext ? 'media' : 'msg', localMessage);
      } else {
        const group = groups[active.id];
        if (!group) return;
        const localMessage: TextMessage = {
          id: 'local', fromMe: true, kind: 'text', text: draft, at: new Date().toISOString(),
          fromSyphraxId: sessionRef.current.realSyphraxId,
          replyTo: replyContext ?? undefined,
        };
        const payload = protocol.encodeVisible({
          kind: 'group_text', group_id: group.id, text: draft,
          ...(replyContext ? { reply_to: replyContext } : {}),
        });
        await sendToGroup(group, payload, 'msg', localMessage);
      }
      setDraft('');
      setReplyTo(null);
    } catch (err) {
      setError(humanizeError(err as Error));
    }
  }

  async function sendFile(file: File, fileName?: string) {
    if (!file || !active) return;
    setError(null);
    setUploading(true);
    try {
      await crypto.ready();
      const fileKey = crypto.newFileKey();
      const plain = new Uint8Array(await file.arrayBuffer());
      const cipher = crypto.encryptFile(plain, fileKey);
      const upload = await api.uploadMedia(cipher);
      const ct = file.type || 'application/octet-stream';
      const kind = kindFromContentType(ct);
      const groupId = active.kind === 'group' ? active.id : undefined;
      const name = fileName || file.name || 'pegado';
      const replyContext = replyTo;
      const payload = protocol.encodeVisible({
        kind, media_id: upload.id, file_key: crypto.b64encode(fileKey),
        name, size: file.size, content_type: ct,
        ...(groupId ? { group_id: groupId } : {}),
        ...(replyContext ? { reply_to: replyContext } : {}),
      });
      const localMessage: MediaMessage = {
        id: 'local', fromMe: true, kind,
        mediaId: upload.id, fileKey, name, size: file.size,
        contentType: ct,
        at: new Date().toISOString(), fromSyphraxId: sessionRef.current.realSyphraxId,
        replyTo: replyContext ?? undefined,
      };
      if (active.kind === 'contact') {
        await sendToContact(active.id, payload, 'media', localMessage);
      } else {
        const group = groups[active.id];
        if (group) await sendToGroup(group, payload, 'media', localMessage);
      }
      setReplyTo(null);
    } catch (err) {
      setError(humanizeError(err as Error));
    } finally {
      setUploading(false);
    }
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await sendFile(file);
  }

  async function onPasteDraft(e: React.ClipboardEvent<HTMLInputElement>) {
    if (!active) return;
    // Camino síncrono: clipboardData.items (Chrome/Firefox/Safari desktop)
    if (e.clipboardData) {
      const items = Array.from(e.clipboardData.items || []);
      const imageItem = items.find((it) => it.kind === 'file' && it.type.startsWith('image/'));
      if (imageItem) {
        const file = imageItem.getAsFile();
        if (file) {
          e.preventDefault();
          const ext = file.type.split('/')[1] || 'png';
          await sendFile(new File([file], `imagen-${Date.now()}.${ext}`, { type: file.type }));
          return;
        }
      }
    }
    // Camino asíncrono: navigator.clipboard.read() (iOS Safari)
    try {
      const cb = (navigator as any).clipboard;
      if (cb && typeof cb.read === 'function') {
        const items = await cb.read();
        for (const item of items) {
          for (const type of item.types) {
            if (typeof type === 'string' && type.startsWith('image/')) {
              const blob = await item.getType(type);
              e.preventDefault();
              const ext = type.split('/')[1] || 'png';
              await sendFile(new File([blob], `imagen-${Date.now()}.${ext}`, { type }));
              return;
            }
          }
        }
      }
    } catch {
      // Si el usuario no concede permisos o no hay imagen, dejamos que el paste normal continúe (texto).
    }
  }

  async function pasteImageFromClipboard() {
    if (!active) return;
    try {
      const cb = (navigator as any).clipboard;
      if (!cb || typeof cb.read !== 'function') {
        setError(t('error.clipboardUnsupported'));
        return;
      }
      const items = await cb.read();
      for (const item of items) {
        for (const type of item.types) {
          if (typeof type === 'string' && type.startsWith('image/')) {
            const blob = await item.getType(type);
            const ext = type.split('/')[1] || 'png';
            await sendFile(new File([blob], `imagen-${Date.now()}.${ext}`, { type }));
            return;
          }
        }
      }
      setError(t('error.noImageInClipboard'));
    } catch (err) {
      setError(t('error.cantPaste'));
    }
  }

  function onClickReply(m: ChatMessage) {
    setEditingMessage(null);
    const fromName = m.fromMe ? sessionRef.current.realSyphraxId : (m.fromSyphraxId ?? '');
    let preview = '';
    if (m.kind === 'text') preview = m.text.slice(0, 120);
    else if (m.kind === 'image') preview = '📷 Imagen';
    else if (m.kind === 'video') preview = '🎬 Vídeo';
    else if (m.kind === 'audio') preview = '🎙 Audio';
    else if (m.kind === 'contact') preview = `👤 ${m.contactSyphraxId}`;
    else preview = `📎 ${(m as MediaMessage).name}`;
    setReplyTo({ id: m.id, preview, from: fromName });
    draftInputRef.current?.focus();
  }

  function onClickEdit(m: ChatMessage) {
    if (!m.fromMe || m.kind !== 'text') return;
    setReplyTo(null);
    setEditingMessage({ id: m.id, originalText: m.text });
    setDraft(m.text);
    draftInputRef.current?.focus();
  }

  function cancelEditOrReply() {
    if (editingMessage) {
      setEditingMessage(null);
      setDraft('');
    }
    setReplyTo(null);
  }

  async function sendReaction(msg: ChatMessage, emoji: string | null) {
    if (!active) return;
    if (active.kind === 'contact' && isBlocked(active.id)) return;
    const myId = sessionRef.current.realSyphraxId;
    setError(null);
    try {
      // Aplicar localmente primero
      const applyLocal = (target: ChatMessage): ChatMessage => {
        const reactions: Record<string, string[]> = { ...(target.reactions ?? {}) };
        for (const e of Object.keys(reactions)) {
          reactions[e] = reactions[e].filter((id) => id !== myId);
          if (reactions[e].length === 0) delete reactions[e];
        }
        if (emoji) {
          reactions[emoji] = [...(reactions[emoji] ?? []), myId];
        }
        return { ...target, reactions } as ChatMessage;
      };

      const groupId = active.kind === 'group' ? active.id : undefined;
      if (active.kind === 'group') {
        setGroupsByFreq((prev) => {
          const fg = prev[currentFreqKey];
          if (!fg || !fg[active.id]) return prev;
          const g = fg[active.id];
          const idx = g.messages.findIndex((mm) => mm.id === msg.id);
          if (idx < 0) return prev;
          const updated = [...g.messages];
          updated[idx] = applyLocal(g.messages[idx]);
          return { ...prev, [currentFreqKey]: { ...fg, [active.id]: { ...g, messages: updated } } };
        });
        const ctrl = protocol.encodeControl({ kind: 'reaction', message_id: msg.id, emoji, group_id: active.id });
        const group = groups[active.id];
        if (!group) return;
        for (const m of group.members) {
          if (m.syphraxId === myId) continue;
          if (isBlocked(m.syphraxId)) continue;
          try {
            const blob = await buildBlobPayload(m.syphraxId, m.publicKey, ctrl, 'ctrl');
            await api.sendMessage(blob);
          } catch (err) { console.warn('reaction failed for', m.syphraxId, err); }
        }
      } else {
        setChatsByFreq((prev) => {
          const fc = prev[currentFreqKey];
          if (!fc || !fc[active.id]) return prev;
          const c = fc[active.id];
          const idx = c.messages.findIndex((mm) => mm.id === msg.id);
          if (idx < 0) return prev;
          const updated = [...c.messages];
          updated[idx] = applyLocal(c.messages[idx]);
          return { ...prev, [currentFreqKey]: { ...fc, [active.id]: { ...c, messages: updated } } };
        });
        const ctrl = protocol.encodeControl({ kind: 'reaction', message_id: msg.id, emoji });
        const pk = await getOrFetchPublicKey(active.id);
        const blob = await buildBlobPayload(active.id, pk, ctrl, 'ctrl');
        await api.sendMessage(blob);
      }
      void groupId;
    } catch (err) {
      setError(humanizeError(err as Error));
    }
  }

  function onClickReact(msg: ChatMessage, emoji: string) {
    const myId = sessionRef.current.realSyphraxId;
    const already = (msg.reactions ?? {})[emoji]?.includes(myId);
    sendReaction(msg, already ? null : emoji);
  }

  async function onShareContact(syphraxIdToShare: string) {
    setShowShareContact(false);
    if (!active) return;
    setError(null);
    try {
      const groupId = active.kind === 'group' ? active.id : undefined;
      const payload = protocol.encodeVisible({
        kind: 'contact', syphrax_id: syphraxIdToShare,
        ...(groupId ? { group_id: groupId } : {}),
      });
      const localMessage: ContactCardMessage = {
        id: 'local', fromMe: true, kind: 'contact',
        contactSyphraxId: syphraxIdToShare,
        at: new Date().toISOString(), fromSyphraxId: sessionRef.current.realSyphraxId,
      };
      if (active.kind === 'contact') {
        await sendToContact(active.id, payload, 'msg', localMessage);
      } else {
        const group = groups[active.id];
        if (group) await sendToGroup(group, payload, 'msg', localMessage);
      }
    } catch (err) {
      setError(humanizeError(err as Error));
    }
  }

  async function onCreateGroup(name: string, memberIds: string[]) {
    setShowCreateGroup(false);
    setError(null);
    try {
      const groupId = uuid();
      const memberPks: Array<{ syphraxId: string; publicKey: Uint8Array }> = [];
      for (const id of memberIds) {
        const pk = await getOrFetchPublicKey(id);
        memberPks.push({ syphraxId: id, publicKey: pk });
      }
      // Yo soy parte del grupo (siempre).
      const myPk = sessionRef.current.publicKey;
      const allMembers = [{ syphraxId: sessionRef.current.realSyphraxId, publicKey: myPk }, ...memberPks];

      // Guardar grupo localmente.
      setGroupsByFreq((prev) => {
        const freqGroups = prev[currentFreqKey] ?? {};
        return {
          ...prev,
          [currentFreqKey]: {
            ...freqGroups,
            [groupId]: { id: groupId, name, members: allMembers, messages: [] },
          },
        };
      });

      // Enviar invite a cada miembro (yo no me invito).
      const inviteMembers = allMembers.map((m) => ({
        syphrax_id: m.syphraxId,
        public_key: crypto.b64encode(m.publicKey),
      }));
      const invitePayload = protocol.encodeControl({
        kind: 'group_invite',
        group_id: groupId,
        group_name: name,
        members: inviteMembers,
        freq_name: null,
      });
      // Enviar invite. Si el ratchet del receiver está desincronizado, el invite se
      // descarta silenciosamente (wrong-secret-key → self-heal → sesiones nuevas).
      // Para garantizar entrega, reenviamos el invite tras 4s con sesión re-sincronizada.
      async function dispatchInvite(memberSyphraxId: string, memberPk: Uint8Array) {
        try {
          const blob = await buildBlobPayload(memberSyphraxId, memberPk, invitePayload, 'ctrl');
          await api.sendMessage(blob);
        } catch (err) {
          console.warn('group invite failed for', memberSyphraxId, (err as Error).message);
        }
      }
      for (const member of memberPks) {
        await dispatchInvite(member.syphraxId, member.publicKey);
      }
      // Re-invite tras 4s — la sesión ratchet pudo haberse re-sincronizado en este lapso.
      window.setTimeout(() => {
        for (const member of memberPks) {
          dispatchInvite(member.syphraxId, member.publicKey);
        }
      }, 4000);
      setActive({ kind: 'group', id: groupId });
    } catch (err) {
      setError(humanizeError(err as Error));
    }
  }

  function onPickSticker(emoji: string) {
    // Inserta el emoji en la posición actual del cursor del input.
    const input = draftInputRef.current;
    if (input) {
      const start = input.selectionStart ?? draft.length;
      const end = input.selectionEnd ?? draft.length;
      const next = draft.slice(0, start) + emoji + draft.slice(end);
      setDraft(next);
      // Restaurar foco y posición tras el render.
      requestAnimationFrame(() => {
        input.focus();
        const pos = start + emoji.length;
        input.setSelectionRange(pos, pos);
      });
    } else {
      setDraft((d) => d + emoji);
    }
  }

  function requestEnablePush() {
    let already = false;
    try { already = localStorage.getItem('syphrax_push_explained') === '1'; } catch {}
    if (already) {
      enablePush();
    } else {
      setShowPushIntro(true);
    }
  }

  async function enablePush() {
    try { localStorage.setItem('syphrax_push_explained', '1'); } catch {}
    setError(null);
    const myTag = crypto.b64encode(crypto.generalTag(sessionRef.current.syphraxId));
    const r = await setupPushSubscription(sessionRef.current.token, myTag);
    if (!r.ok) {
      const msg =
        r.reason === 'ios_too_old' ? t('error.push.iosTooOld') :
        r.reason === 'ios_not_standalone' ? t('error.push.iosNotStandalone') :
        r.reason === 'denied' ? 'Has denegado el permiso de notificaciones.' :
        r.reason === 'no_sw' ? 'Este navegador no soporta service workers.' :
        r.reason === 'no_push' ? 'Este navegador no soporta notificaciones push.' :
        r.reason === 'no_vapid' ? 'El servidor no entregó la clave VAPID.' :
        `No se pudo activar (${r.reason}).`;
      setError(msg);
    } else {
      setPushEnabled(true);
      try { localStorage.setItem('syphrax_push_on', '1'); } catch {}
    }
  }

  async function disablePush() {
    await clearPushSubscription(sessionRef.current.token);
    setPushEnabled(false);
    try { localStorage.removeItem('syphrax_push_on'); } catch {}
  }

  function serializeChatRecord(byContact: Record<string, ChatState>) {
    return Object.fromEntries(
      Object.entries(byContact).map(([cid, c]) => [
        cid,
        {
          contactId: c.contactId,
          contactPublicKey: crypto.b64encode(c.contactPublicKey),
          messages: c.messages.map(serializeMessage),
          confirmed: c.confirmed,
        },
      ]),
    );
  }
  function serializeGroupRecord(byGroup: Record<string, GroupState>) {
    return Object.fromEntries(
      Object.entries(byGroup).map(([gid, g]) => [
        gid,
        {
          id: g.id,
          name: g.name,
          members: g.members.map((m) => ({ syphraxId: m.syphraxId, publicKey: crypto.b64encode(m.publicKey) })),
          messages: g.messages.map(serializeMessage),
        },
      ]),
    );
  }

  // Vault: SOLO datos de frecuencia general. Si te roban el dispositivo y saben tu PIN,
  // ven la frecuencia general y nada más. Las frecuencias secretas viven en blobs aparte
  // cifrados con la propia frecuencia.
  async function persistVaultNow() {
    const pin = vaultPinRef.current;
    if (!pin) return;
    const generalChats = chatsByFreqRef.current['__general__'] ?? {};
    const generalGroups = groupsByFreqRef.current['__general__'] ?? {};
    // pubKeyCache + contactProfiles se filtran a contactos que aparecen en general.
    const generalContactIds = new Set<string>();
    for (const cid of Object.keys(generalChats)) generalContactIds.add(cid);
    for (const g of Object.values(generalGroups)) {
      for (const m of g.members) generalContactIds.add(m.syphraxId);
    }
    const filteredPubKey: Record<string, string> = {};
    for (const [id, pk] of pubKeyCacheRef.current.entries()) {
      if (generalContactIds.has(id)) filteredPubKey[id] = crypto.b64encode(pk);
    }
    const filteredProfiles: Record<string, storage.ContactProfileEntry> = {};
    for (const [id, prof] of Object.entries(contactProfilesRef.current)) {
      if (generalContactIds.has(id)) filteredProfiles[id] = prof;
    }
    // Modelo simple (2026-05-04): sin signing/prekey/ratchet en vault.
    const data: storage.VaultData = {
      session: {
        syphraxId: sessionRef.current.syphraxId,
        token: sessionRef.current.token,
        publicKey: crypto.b64encode(sessionRef.current.publicKey),
        privateKey: crypto.b64encode(sessionRef.current.privateKey),
        // Persistimos wrappingKey (Argon2id del password) en el vault para que tras
        // unlock con PIN podamos descifrar archive_chunks. Sin esto, los SALIENTES
        // del usuario desaparecían tras logout-login con PIN (bug 2026-05-04).
        wrappingKey: sessionRef.current.wrappingKey
          ? crypto.b64encode(sessionRef.current.wrappingKey)
          : undefined,
      },
      chats: serializeChatRecord(generalChats),
      groups: serializeGroupRecord(generalGroups),
      pubKeyCache: filteredPubKey,
      settings: settingsRef.current,
      contactProfiles: filteredProfiles,
    };
    await storage.saveVault(pin, data);
  }

  // Freq blob: cifrado con la freqKey (Argon2id de la frecuencia), no con el PIN.
  // Vault PIN compromise no permite descifrar este blob: hace falta la frecuencia.
  // @ts-expect-error v113 unused: pending refactor para usar archive_chunks por freq.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function persistFreqBlobNow() {
    const fq = frequencyRef.current;
    if (!fq) return;
    const freqChats = chatsByFreqRef.current[fq.name] ?? {};
    const freqGroups = groupsByFreqRef.current[fq.name] ?? {};
    const freqContactIds = new Set<string>();
    for (const cid of Object.keys(freqChats)) freqContactIds.add(cid);
    for (const g of Object.values(freqGroups)) {
      for (const m of g.members) freqContactIds.add(m.syphraxId);
    }
    const filteredPubKey: Record<string, string> = {};
    for (const [id, pk] of pubKeyCacheRef.current.entries()) {
      if (freqContactIds.has(id)) filteredPubKey[id] = crypto.b64encode(pk);
    }
    const filteredProfiles: Record<string, storage.ContactProfileEntry> = {};
    for (const [id, prof] of Object.entries(contactProfilesRef.current)) {
      if (freqContactIds.has(id)) filteredProfiles[id] = prof;
    }
    const data: storage.FreqBlobData = {
      chats: serializeChatRecord(freqChats),
      groups: serializeGroupRecord(freqGroups),
      pubKeyCache: filteredPubKey,
      contactProfiles: filteredProfiles,
    };
    await storage.saveFreqBlob(fq.key, data);
  }

  async function enableVault(pin: string) {
    setShowRememberDevice(false);
    setVaultPin(pin);
    // Persistencia inmediata para que se cree la entrada IDB.
    setTimeout(() => persistVaultNow().catch((err) => setError(humanizeError(err as Error))), 0);
  }

  async function handleLogout() {
    // (sin log)
    try {
      const inflight = Array.from(pendingWritesRef.current);
      if (inflight.length > 0) {
        // (sin log)
        await Promise.allSettled(inflight);
      }
      // (sin log: chats keys son syphrax_ids de contactos)
      await persistVaultNow();
      // (sin log)
    } catch (err) {
      console.warn('logout flush failed (continuing anyway):', err);
    }
    onLogout();
  }

  async function forgetDevice() {
    setShowConfirmForget(true);
  }

  async function doForgetDevice() {
    setShowConfirmForget(false);
    await storage.clearVault();
    setVaultPin(null);
  }

  async function activateFrequency(name: string) {
    setShowFreqModal(false);
    setError(null);
    if (session.pseudoOf) {
      // Ya estamos en una freq; primero volvemos a base.
      onSwitchSession(leaveFrequency(baseSession));
    }
    try {
      // Cierra WS para evitar que la sesión vieja siga consumiendo mensajes con
      // el JWT antiguo durante el swap.
      ws.close();
      const newSession = await enterFrequency(baseSession, name, name);
      onSwitchSession(newSession);
    } catch (err) {
      setError(humanizeError(err as Error));
    }
  }

  function deactivateFrequency() {
    if (activeCallRef.current) endCall('hangup');
    if (!session.pseudoOf) return; // ya estamos en base
    ws.close();
    onSwitchSession(leaveFrequency(baseSession));
  }

  // ============== LLAMADAS ==============
  async function sendCallCtrl(peerId: string, peerPk: Uint8Array, ctrl: protocol.ControlPayload) {
    try {
      const plaintext = protocol.encodeControl(ctrl);
      // Señalización de llamada: TTL 1h. Ya no sirven después.
      const blob = await buildBlobPayload(peerId, peerPk, plaintext, 'ctrl', 60 * 60);
      // Solo call_offer dispara push 'call' (llamada entrante). Los demás (answer/ice/end)
      // ocurren con cliente activo o cierran una llamada en curso — no necesitan despertar al peer.
      const pushKind: 'call' | 'silent' = ctrl.kind === 'call_offer' ? 'call' : 'silent';
      await api.sendMessage({ ...blob, push_kind: pushKind });
    } catch (err) {
      console.warn('call ctrl failed:', err);
    }
  }

  // Long-press para entrar en modo seleccion (estilo WhatsApp).
  const longPressTimerRef = useRef<number | null>(null);
  function makeLongPressHandlers(key: string, onShortClick: () => void) {
    return {
      onPointerDown: () => {
        if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = window.setTimeout(() => {
          longPressTimerRef.current = null;
          setSelection((prev) => { const n = new Set(prev); n.add(key); return n; });
          if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
            try { navigator.vibrate(40); } catch {}
          }
        }, 450);
      },
      onPointerUp: () => {
        if (longPressTimerRef.current) {
          window.clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
          if (selection.size > 0) {
            // En modo seleccion, click toggle.
            setSelection((prev) => {
              const n = new Set(prev);
              if (n.has(key)) n.delete(key); else n.add(key);
              return n;
            });
          } else {
            onShortClick();
          }
        }
      },
      onPointerLeave: () => {
        if (longPressTimerRef.current) { window.clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
      },
      onPointerCancel: () => {
        if (longPressTimerRef.current) { window.clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
      },
    };
  }

  function toggleBlock(syphraxId: string, unblock: boolean) {
    setSettings((prev) => {
      const lower = syphraxId.toLowerCase();
      const cur = prev.blocked ?? [];
      const next = unblock ? cur.filter((x) => x !== lower) : (cur.includes(lower) ? cur : [...cur, lower]);
      return { ...prev, blocked: next };
    });
  }

  function saveContactNote(syphraxId: string, alias: string, notes: string) {
    setSettings((prev) => {
      const cleanedAlias = alias.trim();
      const cleanedNotes = notes.trim();
      const cur = { ...(prev.contactNotes ?? {}) };
      if (!cleanedAlias && !cleanedNotes) {
        delete cur[syphraxId];
      } else {
        cur[syphraxId] = {
          ...(cleanedAlias ? { alias: cleanedAlias } : {}),
          ...(cleanedNotes ? { notes: cleanedNotes } : {}),
        };
      }
      return { ...prev, contactNotes: cur };
    });
  }

  // Heal: confirmedRef puede estar desincronizado con chatsByFreq[*][*].confirmed
  // (bug historico de v62 que borraba el set al eliminar conversacion). Al montar,
  // recompongo el set como union de ambas fuentes para que "Mis contactos" muestre
  // todo lo que el usuario considera confirmado.
  useEffect(() => {
    let healed = false;
    for (const [freqKey, byContact] of Object.entries(chatsByFreq)) {
      let set = confirmedRef.current.get(freqKey);
      for (const [cid, c] of Object.entries(byContact)) {
        if (!c.confirmed) continue;
        if (!set) { set = new Set(); confirmedRef.current.set(freqKey, set); }
        if (!set.has(cid)) { set.add(cid); healed = true; }
      }
    }
    if (healed) {
      // Forzar persistencia de prefs (debounce ya lo recogera).
      setSettings((prev) => ({ ...prev }));
    }
  }, [chatsByFreq]);

  function deleteConversation(target: { kind: 'contact' | 'group'; id: string }) {
    const fk = currentFreqKey;
    if (target.kind === 'contact') {
      // Borramos SOLO el chat (mensajes). El contacto sigue siendo "confirmado"
      // y su perfil (nombre, foto) se conserva: si el peer escribe de nuevo o
      // queremos retomar la conversacion via "Mis contactos", no aparece como
      // desconocido. Esto es lo que hace WhatsApp: borrar chat != borrar contacto.
      setChatsByFreq((prev) => {
        const fc = prev[fk];
        if (!fc || !fc[target.id]) return prev;
        const next = { ...fc };
        delete next[target.id];
        const out = { ...prev, [fk]: next };
        chatsByFreqRef.current = out;
        return out;
      });
    } else {
      setGroupsByFreq((prev) => {
        const fg = prev[fk];
        if (!fg || !fg[target.id]) return prev;
        const next = { ...fg };
        delete next[target.id];
        const out = { ...prev, [fk]: next };
        groupsByFreqRef.current = out;
        return out;
      });
    }
    if (active && active.kind === target.kind && active.id === target.id) {
      setActive(null);
    }
  }

  function setupPcHandlers(pc: RTCPeerConnection) {
    pc.onicecandidate = (ev) => {
      if (!ev.candidate) return;
      const call = activeCallRef.current;
      if (!call) return;
      sendCallCtrl(call.peerId, call.peerPublicKey, {
        kind: 'call_ice',
        call_id: call.callId,
        candidate: ev.candidate.candidate,
        sdp_mid: ev.candidate.sdpMid,
        sdp_m_line_index: ev.candidate.sdpMLineIndex,
      });
    };
    pc.ontrack = (ev) => {
      const stream = ev.streams && ev.streams[0];
      if (!stream) return;
      setActiveCall((prev) => prev ? { ...prev, remoteStream: stream } : prev);
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === 'connected') {
        setActiveCall((prev) => prev ? { ...prev, state: 'connected', startedAt: prev.startedAt || new Date().toISOString() } : prev);
      } else if (st === 'failed' || st === 'closed' || st === 'disconnected') {
        if (st === 'failed') cleanupCall();
      }
    };
  }

  async function startCall(receiverId: string, hasVideo: boolean) {
    if (activeCallRef.current) return;
    const callId = uuid();
    try {
      const stream = await webrtc.getLocalMedia(hasVideo);
      const pk = await getOrFetchPublicKey(receiverId);
      const pc = new RTCPeerConnection({ iceServers: await webrtc.getIceServers() });
      pcRef.current = pc;
      const initial: ActiveCallState = {
        callId, peerId: receiverId, peerPublicKey: pk,
        direction: 'outgoing', hasVideo,
        state: 'ringing', startedAt: null,
        micMuted: false, cameraOn: hasVideo,
        remoteStream: null, localStream: stream,
      };
      activeCallRef.current = initial;
      setActiveCall(initial);
      setupPcHandlers(pc);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: hasVideo });
      await pc.setLocalDescription(offer);
      await sendCallCtrl(receiverId, pk, {
        kind: 'call_offer',
        call_id: callId,
        sdp: offer.sdp || '',
        has_video: hasVideo,
        freq_name: null,
      });
    } catch (err) {
      setError(t('error.cantStartCall', (err as Error).message));
      cleanupCall();
    }
  }

  async function acceptCall() {
    const off = incomingOfferRef.current;
    const call = activeCallRef.current;
    if (!off || !call) return;
    try {
      const stream = await webrtc.getLocalMedia(off.hasVideo);
      const pc = new RTCPeerConnection({ iceServers: await webrtc.getIceServers() });
      pcRef.current = pc;
      setupPcHandlers(pc);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await pc.setRemoteDescription({ type: 'offer', sdp: off.sdp });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await sendCallCtrl(off.senderId, off.senderPk, {
        kind: 'call_answer', call_id: off.callId, sdp: answer.sdp || '',
      });
      // Aplicar candidatos ICE pendientes (que pudieron llegar antes de remoteDescription)
      for (const c of pendingIceRef.current) {
        try { await pc.addIceCandidate(c); } catch (_e) { /* ignore */ }
      }
      pendingIceRef.current = [];
      incomingOfferRef.current = null;
      setActiveCall((prev) => prev ? { ...prev, state: 'connecting', localStream: stream } : prev);
    } catch (err) {
      setError(t('error.cantAcceptCall', (err as Error).message));
      // Avisar al emisor que no pudimos
      if (off) {
        await sendCallCtrl(off.senderId, off.senderPk, { kind: 'call_end', call_id: off.callId, reason: 'failed' });
      }
      cleanupCall();
    }
  }

  function rejectCall() {
    const off = incomingOfferRef.current;
    const call = activeCallRef.current;
    if (off) {
      sendCallCtrl(off.senderId, off.senderPk, { kind: 'call_end', call_id: off.callId, reason: 'rejected' });
    } else if (call) {
      sendCallCtrl(call.peerId, call.peerPublicKey, { kind: 'call_end', call_id: call.callId, reason: 'rejected' });
    }
    cleanupCall();
  }

  function endCall(reason: 'hangup' | 'cancelled' = 'hangup') {
    const call = activeCallRef.current;
    if (call) {
      sendCallCtrl(call.peerId, call.peerPublicKey, { kind: 'call_end', call_id: call.callId, reason });
    }
    cleanupCall();
  }

  function cleanupCall() {
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (_e) { /* ignore */ }
      pcRef.current = null;
    }
    const call = activeCallRef.current;
    if (call?.localStream) {
      call.localStream.getTracks().forEach((t) => t.stop());
    }
    pendingIceRef.current = [];
    incomingOfferRef.current = null;
    activeCallRef.current = null;
    setActiveCall(null);
  }

  function toggleMic() {
    const call = activeCallRef.current;
    if (!call?.localStream) return;
    const t = call.localStream.getAudioTracks()[0];
    if (!t) return;
    t.enabled = !t.enabled;
    setActiveCall((prev) => prev ? { ...prev, micMuted: !t.enabled } : prev);
  }

  function toggleCamera() {
    const call = activeCallRef.current;
    if (!call?.localStream) return;
    const t = call.localStream.getVideoTracks()[0];
    if (!t) return;
    t.enabled = !t.enabled;
    setActiveCall((prev) => prev ? { ...prev, cameraOn: t.enabled } : prev);
  }

  // Ringtone: bucle de tonos mientras la llamada está en estado ringing.
  const ringCtxRef = useRef<{ ctx: AudioContext; timer: number } | null>(null);
  useEffect(() => {
    function stopRing() {
      const r = ringCtxRef.current;
      if (!r) return;
      window.clearInterval(r.timer);
      try { r.ctx.close(); } catch (_e) { /* ignore */ }
      ringCtxRef.current = null;
    }
    if (!activeCall || activeCall.state !== 'ringing') {
      stopRing();
      return;
    }
    if (ringCtxRef.current) return; // ya está sonando
    try {
      const Ctor: any = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctor();
      function beep() {
        const t0 = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = activeCall && activeCall.direction === 'incoming' ? 660 : 440;
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(0.15, t0 + 0.05);
        gain.gain.linearRampToValueAtTime(0, t0 + 0.45);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(t0); osc.stop(t0 + 0.5);
      }
      beep();
      const timer = window.setInterval(beep, 1500);
      ringCtxRef.current = { ctx, timer };
    } catch (_e) { /* ignore audio errors */ }
    return stopRing;
  }, [activeCall?.state, activeCall?.direction]);


  const modeLabel = frequency ? t('sidebar.freq', frequency.name) : t('sidebar.general');

  function displayNameOf(syphraxId: string): string {
    // Prioridad: alias local > displayName del peer > syphraxId.
    const alias = settingsRef.current.contactNotes?.[syphraxId]?.alias;
    if (alias && alias.trim()) return alias.trim();
    const p = contactProfilesRef.current[syphraxId];
    if (p && p.displayName) return p.displayName;
    return syphraxId;
  }

  function photoOf(syphraxId: string): string | null {
    return contactProfilesRef.current[syphraxId]?.photoB64 ?? null;
  }

  function lastSummary(messages: ChatMessage[]): string {
    if (messages.length === 0) return '—';
    const m = messages[messages.length - 1];
    const prefix = m.fromSyphraxId && !m.fromMe ? `${m.fromSyphraxId}: ` : m.fromMe ? '' : '';
    if (m.kind === 'text') return prefix + m.text.slice(0, 60);
    if (m.kind === 'image') return prefix + t('preview.image');
    if (m.kind === 'video') return prefix + t('preview.video');
    if (m.kind === 'audio') return prefix + t('preview.audio');
    if (m.kind === 'contact') return prefix + `👤 ${m.contactSyphraxId}`;
    return prefix + `📎 ${m.name}`;
  }

  const activeChat = active?.kind === 'contact' ? chats[active.id] : null;
  const activeGroup = active?.kind === 'group' ? groups[active.id] : null;
  const activeMessages = activeChat?.messages ?? activeGroup?.messages ?? [];
  const activeTitle = activeChat
    ? displayNameOf(activeChat.contactId)
    : activeGroup ? `${activeGroup.name} · ${activeGroup.members.length} miembros` : '';

  if (showSettings) {
    return (
      <Settings
        session={session}
        settings={settings}
        activeFrequency={frequency ? frequency.name : null}
        onChange={(next) => {
          setSettings(next);
          // Si cambió el perfil, propagamos a contactos de la frecuencia activa.
          broadcastProfileToCurrentFrequency();
        }}
        onAfterPasswordChanged={() => { /* claves locales no cambian, solo el wrapping en servidor */ }}
        onClose={() => setShowSettings(false)}
      />
    );
  }

  return (
    <div className={active ? 'chat has-active' : 'chat'}>
      <aside className="sidebar">
        {inSelection ? (
          <header className="selection-bar">
            <button className="icon-btn" onClick={() => setSelection(new Set())} title="Cancelar" aria-label="Cancelar">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
            </button>
            <span className="selection-count">{selection.size}</span>
            <button className="icon-btn danger" onClick={() => setConfirmDeleteMulti(true)} title="Eliminar" aria-label="Eliminar">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
            </button>
          </header>
        ) : (
          <header>
            <div className="me">
              <div className="my-id">{session.realSyphraxId}</div>
              <div className={frequency ? 'my-mode active' : 'my-mode'}>{modeLabel}</div>
            </div>
            <button className="icon-btn" onClick={() => setShowContactsModal(true)} title="Mis contactos" aria-label="Mis contactos">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </button>
            <button className="ghost" onClick={() => setShowSettings(true)} title={t('sidebar.settings')}>⚙</button>
            <button className="ghost" onClick={handleLogout} title={t('sidebar.tooltip.logout')}>{t('sidebar.logout')}</button>
          </header>
        )}
        <div className="freq-bar">
          {frequency ? (
            <button className="freq-active" onClick={deactivateFrequency} title={t('sidebar.general')}>
              <span className="dot" /> {t('sidebar.exitFreq', frequency.name)}
            </button>
          ) : (
            <button className="freq-tune" onClick={() => setShowFreqModal(true)}>
              {t('sidebar.tune')}
            </button>
          )}
        </div>
        <div className="device-row">
          {vaultPin ? (
            <button className="ghost danger" onClick={forgetDevice} title={t('sidebar.forget')}>
              🗝 {t('sidebar.forget')}
            </button>
          ) : (
            <button className="ghost" onClick={() => setShowRememberDevice(true)} title={t('sidebar.remember')}>
              🔐 {t('sidebar.remember')}
            </button>
          )}
          {pushEnabled ? (
            <button className="ghost notif-on" onClick={disablePush} title={t('push.btnDisable')}>
              <span className="notif-dot" /> {t('push.btnOn')}
            </button>
          ) : (
            <button className="ghost" onClick={requestEnablePush} title={t('push.btnEnable')}>
              🔕 {t('push.btnOff')}
            </button>
          )}
        </div>
        <div className="add-contact">
          <input
            placeholder={t('sidebar.addContact')}
            value={newContact}
            onChange={(e) => setNewContact(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addContact()}
            autoCapitalize="none"
            autoCorrect="off"
          />
          <button onClick={addContact}>+</button>
        </div>
        <div className="new-group-row">
          <button className="ghost" onClick={() => setShowCreateGroup(true)} disabled={Object.keys(chats).length === 0}>
            ➕ {t('sidebar.newGroup')}
          </button>
        </div>
        <ul className="contacts">
          {Object.values(groups).map((g) => {
            const unread = unreadOfGroup(g);
            const isActive = active?.kind === 'group' && active.id === g.id;
            const cls = ['group-item'];
            if (isActive) cls.push('active');
            if (unread > 0 && !isActive) cls.push('has-unread');
            return (
              <li
                key={g.id}
                className={(selection.has('group:' + g.id) ? cls.concat(['selected']).join(' ') : cls.join(' '))}
                {...makeLongPressHandlers('group:' + g.id, () => setActive({ kind: 'group', id: g.id }))}
              >
                <div className="contact-row">
                  <div className="contact-text">
                    <div className="contact-id">👥 {g.name}</div>
                    <div className="contact-last">{lastSummary(g.messages)}</div>
                  </div>
                  {unread > 0 && !isActive && (
                    <span className="unread-badge" aria-label={String(unread) + " mensajes sin leer"}>
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
          {Object.values(chats).map((c) => {
            const photo = photoOf(c.contactId);
            const name = displayNameOf(c.contactId);
            const unread = unreadOfChat(c);
            const isActive = active?.kind === 'contact' && active.id === c.contactId;
            const cls: string[] = [];
            if (isActive) cls.push('active');
            if (unread > 0 && !isActive) cls.push('has-unread');
            return (
              <li
                key={c.contactId}
                className={(selection.has('contact:' + c.contactId) ? cls.concat(['selected']).join(' ') : cls.join(' '))}
                {...makeLongPressHandlers('contact:' + c.contactId, () => {
                  setActive({ kind: 'contact', id: c.contactId });
                  void markChatReadInDb(c.contactId);
                })}
              >
                <div className="contact-row">
                  <div className="avatar small">{photo ? <img src={photo} alt="" /> : <span>{(name || '?').slice(0, 1).toUpperCase()}</span>}</div>
                  <div className="contact-text">
                    <div className="contact-id">{name}</div>
                    {name !== c.contactId && <div className="contact-sub">{c.contactId}</div>}
                    <div className="contact-last">{lastSummary(c.messages)}</div>
                  </div>
                  {unread > 0 && !isActive && (
                    <span className="unread-badge" aria-label={String(unread) + " mensajes sin leer"}>
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {error && <div className="error">{error}</div>}
      </aside>
      <main className="chat-window">
        {!active && Object.keys(chats).length === 0 && Object.keys(groups).length === 0 && (
          <div className="empty welcome-card">
            <h2>{t('welcome.title')}</h2>
            <p>{t('welcome.concept')}</p>
            <ol className="welcome-steps">
              <li>{t('welcome.step1')}</li>
              <li>{t('welcome.step2')}</li>
              <li>{t('welcome.step3')}</li>
            </ol>
            <p className="hint welcome-note">{t('welcome.multidevice')}</p>
            <div className="welcome-actions">
              <button className="primary" type="button" onClick={() => setShowFreqModal(true)}>
                {t('welcome.btnFreq')}
              </button>
              <button className="ghost" type="button" onClick={() => setShowContactsModal(true)}>
                {t('welcome.btnContacts')}
              </button>
            </div>
          </div>
        )}
        {!active && (Object.keys(chats).length > 0 || Object.keys(groups).length > 0) && (
          <div className="empty">{t('chat.empty')}</div>
        )}
        {active && (
          <>
            <header className="chat-header">
              <div className="chat-header-main">
                <button className="back-btn" onClick={() => setActive(null)} title={t('common.back')} aria-label={t('common.back')}>←</button>
                {active.kind === 'contact' && (() => {
                  const photo = photoOf(active.id);
                  const name = displayNameOf(active.id);
                  return (
                    <div className="avatar small">
                      {photo ? <img src={photo} alt="" /> : <span>{(name || '?').slice(0, 1).toUpperCase()}</span>}
                    </div>
                  );
                })()}
                <div className="chat-header-text">
                  <div className="chat-header-title">{activeTitle}</div>
                  {active.kind === 'contact' && activePresence && !(settings.blocked ?? []).includes(active.id.toLowerCase()) && (
                    <div className="chat-header-presence">
                      {activePresence.online && activePresence.sameFrequency
                        ? t('chat.online')
                        : activePresence.last_seen_in_freq
                          ? t('chat.lastSeen', new Date(activePresence.last_seen_in_freq).toLocaleString())
                          : ''}
                    </div>
                  )}
                  {active.kind === 'contact' && (settings.blocked ?? []).includes(active.id.toLowerCase()) && (
                    <div className="chat-header-presence chat-header-blocked">Bloqueado</div>
                  )}
                </div>
              </div>
              <div className="chat-header-actions">
                {active.kind === 'contact' && !(settings.blocked ?? []).includes(active.id.toLowerCase()) && (
                  <>
                    <button className="icon-btn" title={t('chat.tooltip.callAudio')} onPointerDown={(e) => { e.preventDefault(); startCall(active.id, false); }} aria-label={t('chat.tooltip.callAudio')}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    </button>
                    <button className="icon-btn" title={t('chat.tooltip.callVideo')} onPointerDown={(e) => { e.preventDefault(); startCall(active.id, true); }} aria-label={t('chat.tooltip.callVideo')}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                    </button>
                  </>
                )}
              </div>
            </header>
            {active.kind === 'contact' && activeChat && !activeChat.confirmed && (
              <div className="unconfirmed-banner">
                <span>{t('chat.unconfirmedBanner')}</span>
                <button className="primary confirm-contact" onClick={() => confirmContact(active.id, currentFreqKey)}>
                  {t('chat.confirmContact')}
                </button>
              </div>
            )}
            <div className="messages" ref={messagesRef} onScroll={onMessagesScroll}>
              {[...activeMessages].reverse().map((m) => (
                <MessageBubble
                  key={m.id}
                  msg={m}
                  showSender={active?.kind === 'group' && !m.fromMe}
                  senderDisplayName={m.fromSyphraxId ? displayNameOf(m.fromSyphraxId) : undefined}
                  resolveDisplayName={displayNameOf}
                  myId={session.realSyphraxId}
                  onAddContact={addContactFromCard}
                  onReply={onClickReply}
                  onEdit={onClickEdit}
                  onReact={onClickReact}
                  isMenuOpen={openMenuId === m.id}
                  onToggleMenu={toggleBubbleMenu}
                />
              ))}
            </div>
            {(replyTo || editingMessage) && (
              <div className="compose-context">
                <div className="compose-context-bar">
                  <span className="compose-context-icon">{editingMessage ? '✎' : '↩'}</span>
                  <div className="compose-context-text">
                    <div className="compose-context-title">{editingMessage ? t('chat.editing') : t('chat.replyingTo', displayNameOf(replyTo!.from))}</div>
                    <div className="compose-context-preview">{editingMessage ? editingMessage.originalText : replyTo!.preview}</div>
                  </div>
                  <button className="ghost" onClick={cancelEditOrReply} title="Cancelar">✕</button>
                </div>
              </div>
            )}
            {active.kind === 'contact' && (settings.blocked ?? []).includes(active.id.toLowerCase()) ? (
              <div className="blocked-composer">
                <div className="blocked-composer-text">
                  Has bloqueado a {displayNameOf(active.id)}. No puedes enviarle mensajes ni llamarle.
                </div>
                <button
                  type="button"
                  className="blocked-composer-unblock"
                  onClick={() => setConfirmBlock({ id: active.id, unblock: true })}
                >Desbloquear</button>
              </div>
            ) : (
            <footer>
              <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onPickFile} />
              <button className="ghost attach" title={t('chat.tooltip.attach')} onMouseDown={(e) => e.preventDefault()} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? '…' : '📎'}
              </button>
              <button className="ghost attach" title={t('chat.tooltip.shareContact')} onMouseDown={(e) => e.preventDefault()} onClick={() => setShowShareContact(true)} disabled={uploading}>
                👤
              </button>
              <button className="ghost attach" title={t('chat.tooltip.paste')} onMouseDown={(e) => e.preventDefault()} onClick={pasteImageFromClipboard} disabled={uploading}>
                📋
              </button>
              <div className="sticker-anchor">
                <button
                  className="ghost attach"
                  title={t('chat.tooltip.stickers')}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowStickerPicker((s) => !s)}
                  disabled={uploading}
                >
                  😀
                </button>
                {showStickerPicker && (
                  <StickerPicker onPick={onPickSticker} onClose={() => setShowStickerPicker(false)} />
                )}
              </div>
              <input
                ref={draftInputRef}
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
                onPaste={onPasteDraft}
                placeholder={editingMessage ? t('chat.editPlaceholder') : t('chat.placeholder')}
                disabled={uploading}
                enterKeyHint="send"
                autoCapitalize="sentences"
                autoCorrect="on"
                autoComplete="off"
              />
              <button
                type="button"
                className="primary"
                onPointerDown={(e) => {
                  e.preventDefault();
                  if (draft.trim() && !uploading) send();
                }}
                disabled={!draft.trim() || uploading}
              >{t('chat.btn.send')}</button>
            </footer>
            )}
          </>
        )}
      </main>
      {showFreqModal && (
        <FrequencyModal onCancel={() => setShowFreqModal(false)} onActivate={activateFrequency} />
      )}
      {showShareContact && (
        <ShareContactModal
          contacts={Object.values(chats).filter((c) => active?.kind !== 'contact' || c.contactId !== active.id).map((c) => c.contactId)}
          onCancel={() => setShowShareContact(false)}
          onShare={onShareContact}
        />
      )}
      {confirmDeleteMulti && selection.size > 0 && (
        <div className="modal-backdrop" onClick={() => setConfirmDeleteMulti(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{selection.size === 1 ? 'Eliminar conversacion' : 'Eliminar ' + selection.size + ' conversaciones'}</h2>
            <p className="hint">
              Se borraran los mensajes localmente y de tu historial cifrado en el server. Esta accion no se puede deshacer.
            </p>
            <div className="modal-actions">
              <button type="button" className="ghost" onClick={() => setConfirmDeleteMulti(false)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="primary danger"
                onClick={() => {
                  const list = Array.from(selection);
                  setConfirmDeleteMulti(false);
                  setSelection(new Set());
                  for (const key of list) {
                    const colon = key.indexOf(':');
                    const kind = key.slice(0, colon) as 'contact' | 'group';
                    const id = key.slice(colon + 1);
                    deleteConversation({ kind, id });
                  }
                }}
              >Eliminar</button>
            </div>
          </div>
        </div>
      )}
                  {showContactsModal && (() => {
        // Mostrar TODOS los chats (confirmados y no confirmados) + ids en confirmedRef
        // + ids con nota guardada. Asi el usuario ve a quien le ha escrito o quien
        // le ha escrito, aunque la confirmacion E2E aun no se haya cerrado.
        const set = confirmedRef.current.get(currentFreqKey);
        const merged = new Set<string>(set ? Array.from(set) : []);
        const fc = chatsByFreq[currentFreqKey] ?? {};
        for (const cid of Object.keys(fc)) merged.add(cid);
        for (const cid of Object.keys(settings.contactNotes ?? {})) merged.add(cid);
        const allIds = Array.from(merged);
        const isConfirmedOf = (id: string): boolean => {
          if (set && set.has(id)) return true;
          const c = fc[id];
          return !!(c && c.confirmed);
        };
        const q = contactsSearch.trim().toLowerCase();
        const filtered = q
          ? allIds.filter((id) => id.toLowerCase().includes(q) || displayNameOf(id).toLowerCase().includes(q))
          : allIds;
        const ids = filtered.sort((a, b) => displayNameOf(a).localeCompare(displayNameOf(b)));
        return (
          <div className="modal-backdrop" onClick={() => { setShowContactsModal(false); setEditingContact(null); }}>
            <div className="modal contacts-modal" onClick={(e) => e.stopPropagation()}>
              {editingContact ? (
                <>
                  <h2>Editar contacto</h2>
                  <p className="hint">{editingContact}</p>
                  <label className="field-label">Alias</label>
                  <input
                    type="text"
                    value={editAliasDraft}
                    onChange={(e) => setEditAliasDraft(e.target.value)}
                    placeholder="Ej. Juanjo (trabajo)"
                    autoFocus
                    maxLength={60}
                  />
                  <label className="field-label">Nota privada</label>
                  <textarea
                    value={editNotesDraft}
                    onChange={(e) => setEditNotesDraft(e.target.value)}
                    placeholder="Solo tú la ves (cifrada en server)."
                    maxLength={500}
                    rows={3}
                  />
                  {(() => {
                    const isBlocked = !!editingContact && (settings.blocked ?? []).includes(editingContact.toLowerCase());
                    return (
                      <button
                        type="button"
                        className={isBlocked ? 'block-toggle-btn unblock' : 'block-toggle-btn block'}
                        onClick={() => setConfirmBlock({ id: editingContact!, unblock: isBlocked })}
                      >
                        {isBlocked ? 'Desbloquear contacto' : 'Bloquear contacto'}
                      </button>
                    );
                  })()}
                  <div className="modal-actions">
                    <button type="button" className="ghost" onClick={() => setEditingContact(null)}>{t('common.cancel')}</button>
                    <button type="button" className="primary" onClick={() => {
                      saveContactNote(editingContact!, editAliasDraft, editNotesDraft);
                      setEditingContact(null);
                    }}>Guardar</button>
                  </div>
                </>
              ) : (
                <>
                  <h2>Mis contactos</h2>
                  <input
                    type="search"
                    value={contactsSearch}
                    onChange={(e) => setContactsSearch(e.target.value)}
                    placeholder="Buscar por alias o syphrax_id"
                    className="contacts-search"
                  />
                  {ids.length === 0 ? (
                    <p className="hint" style={{ textAlign: 'center', padding: '24px 0' }}>
                      {q ? 'Sin resultados.' : 'Aun no tienes contactos.'}
                    </p>
                  ) : (
                    <ul className="contacts contacts-modal-list">
                      {ids.map((id) => {
                        const photo = photoOf(id);
                        const name = displayNameOf(id);
                        const note = settings.contactNotes?.[id]?.notes;
                        const confirmed = isConfirmedOf(id);
                        return (
                          <li key={id} className="contact-modal-item">
                            <div className="contact-row" onClick={() => {
                              const cached = pubKeyCacheRef.current.get(id);
                              if (cached) ensureContactState(id, cached, currentFreqKey, true);
                              setActive({ kind: 'contact', id });
                              setShowContactsModal(false);
                              setContactsSearch('');
                            }}>
                              <div className="avatar small">{photo ? <img src={photo} alt="" /> : <span>{(name || '?').slice(0, 1).toUpperCase()}</span>}</div>
                              <div className="contact-text">
                                <div className="contact-id">
                                  {name}
                                  {(settings.blocked ?? []).includes(id.toLowerCase()) && <span className="contact-badge-blocked">bloqueado</span>}
                                  {!confirmed && !(settings.blocked ?? []).includes(id.toLowerCase()) && <span className="contact-badge-pending">sin confirmar</span>}
                                </div>
                                {name !== id && <div className="contact-sub">{id}</div>}
                                {note && <div className="contact-last">📝 {note}</div>}
                              </div>
                            </div>
                            <button className="icon-btn edit-contact-btn" title="Editar alias y nota" aria-label="Editar" onClick={(e) => {
                              e.stopPropagation();
                              const cur = settings.contactNotes?.[id] ?? {};
                              setEditAliasDraft(cur.alias ?? '');
                              setEditNotesDraft(cur.notes ?? '');
                              setEditingContact(id);
                            }}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <div className="modal-actions">
                    <button type="button" className="ghost" onClick={() => { setShowContactsModal(false); setContactsSearch(''); }}>{t('common.close') || 'Cerrar'}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
            {showPushIntro && (
        <div className="modal-backdrop" onClick={() => setShowPushIntro(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('push.introTitle')}</h2>
            <p className="hint">{t('push.introBody')}</p>
            <ul className="welcome-steps">
              <li>{t('push.introBullet1')}</li>
              <li>{t('push.introBullet2')}</li>
              <li>{t('push.introBullet3')}</li>
            </ul>
            <div className="modal-actions">
              <button type="button" className="ghost" onClick={() => setShowPushIntro(false)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="primary" onClick={() => { setShowPushIntro(false); enablePush(); }}>
                {t('push.introConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
            {showConfirmForget && (
        <div className="modal-backdrop" onClick={() => setShowConfirmForget(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('sidebar.forget')}</h2>
            <p className="hint">{t('auth.confirmForget')}</p>
            <div className="modal-actions">
              <button type="button" className="ghost" onClick={() => setShowConfirmForget(false)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="primary danger" onClick={doForgetDevice}>
                {t('sidebar.forget')}
              </button>
            </div>
          </div>
        </div>
      )}
            {confirmBlock && (
        <div className="modal-backdrop" onClick={() => setConfirmBlock(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{confirmBlock.unblock ? 'Desbloquear contacto' : 'Bloquear contacto'}</h2>
            <p className="hint">
              {confirmBlock.unblock
                ? <>¿Desbloquear a <strong>{displayNameOf(confirmBlock.id)}</strong>? Volverás a recibir sus mensajes.</>
                : <>¿Bloquear a <strong>{displayNameOf(confirmBlock.id)}</strong>?<br />No recibirás más mensajes ni llamadas suyas, y tampoco podrás escribirle.</>}
            </p>
            <div className="modal-actions">
              <button type="button" className="ghost" onClick={() => setConfirmBlock(null)}>{t('common.cancel')}</button>
              <button
                type="button"
                className={confirmBlock.unblock ? 'primary' : 'primary danger'}
                onClick={() => {
                  const tgt = confirmBlock;
                  setConfirmBlock(null);
                  toggleBlock(tgt.id, tgt.unblock);
                  setEditingContact(null);
                }}
              >{confirmBlock.unblock ? 'Desbloquear' : 'Bloquear'}</button>
            </div>
          </div>
        </div>
      )}
            {showCreateGroup && (
        <CreateGroupModal
          contacts={Object.values(chats).map((c) => c.contactId)}
          onCancel={() => setShowCreateGroup(false)}
          onCreate={onCreateGroup}
        />
      )}
      {showRememberDevice && (
        <RememberDeviceModal onCancel={() => setShowRememberDevice(false)} onSave={enableVault} />
      )}
      {activeCall && (
        <CallScreen
          peerName={displayNameOf(activeCall.peerId)}
          peerPhotoB64={photoOf(activeCall.peerId)}
          direction={activeCall.direction}
          state={activeCall.state}
          hasVideo={activeCall.hasVideo}
          startedAt={activeCall.startedAt}
          localStream={activeCall.localStream}
          remoteStream={activeCall.remoteStream}
          micMuted={activeCall.micMuted}
          cameraOn={activeCall.cameraOn}
          onAccept={acceptCall}
          onReject={rejectCall}
          onHangup={() => endCall('hangup')}
          onToggleMic={toggleMic}
          onToggleCamera={toggleCamera}
        />
      )}
      <div className="toasts">
        {toasts.map((t) => (
          <div key={t.id} className="toast">
            <div className="toast-title">{t.title}</div>
            <div className="toast-body">{t.body}</div>
          </div>
        ))}
      </div>
    </div>
  );

  async function addContactFromCard(syphraxId: string) {
    setNewContact(syphraxId);
    const cleaned = syphraxId.trim().toLowerCase();
    try {
      if (!cleaned || cleaned === sessionRef.current.realSyphraxId.toLowerCase()) return;
      const publicKey = await getOrFetchPublicKey(cleaned);
      ensureContactState(cleaned, publicKey, currentFreqKey, true);
      confirmContact(cleaned, currentFreqKey);
      setActive({ kind: 'contact', id: cleaned });
      setNewContact('');
    } catch (err) {
      const e = err as Error;
      if (e.message.includes('HTTP 404') && pseudoOfRef.current) {
        setError(t('error.notInFreq', cleaned));
      } else {
        setError(humanizeError(e));
      }
    }
  }
}
